// SketchOverlay — отрисовывает все эскизы (точки/линии/окружности) ВНУТРИ
// 3D-сцены на их собственных плоскостях.
//
// В режиме view3d — эскизы видны, но не интерактивны (нельзя редактировать).
// В режиме sketch  — активный эскиз интерактивен (drag точек, рисование),
//                    остальные эскизы видны как информация (не активны).
//
// Каждый эскиз размещается через group с `rotation` соответствующим его
// `planeRef.plane` (XY/XZ/YZ).

import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';

import useCadStore from '../store/useCadStore';
// Для инструмента «Текст»: контуры букв считает ядро, ошибки показываем тостом.
import { getGeometryClient } from '../geometryClient';
import { toast } from '../ui/Toasts';
import { getSolverClient } from '../solverClient';
import { sketchToGcsPrimitives, applyGcsResultToSketch } from './sketchToGcs';
import { findProfiles, profilePolygonVertices, offsetPolygonVertices, pointInProfile, profileArea } from './sketchProfiles';
import { computeFillet, linesAtPoint } from './sketchFillet';
import { computeExtend, nearestEnd } from './sketchExtend';
import { mirrorPrimitives, patternPrimitives } from './sketchCopy';
import { projectEdges } from './sketchProject';
import { splineToPolyline } from './sketchSpline';
import { getEdgeEndpointsForBody } from '../bodyHistoryHelper';
import { getSketchAnchorPoints } from './anchorPoints';


let _nextId = 1;
const newId = (prefix) => `${prefix}_${_nextId++}_${Date.now().toString(36)}`;


// Quaternion для поворота group, СОВПАДАЮЩЕГО с replicad sketchOnPlane.
// replicad PLANES_CONFIG:
//   XY: xDir=[1,0,0],     normal=[0, 0, 1]   → yDir = [0, 1, 0]
//   XZ: xDir=[1,0,0],     normal=[0,-1, 0]   → yDir = [0, 0, 1]
//   YZ: xDir=[0,1,0],     normal=[1, 0, 0]   → yDir = [0, 0, 1]
//
// Сборка матрицы из базисных векторов через Matrix4.makeBasis(x, y, z).
function planeQuaternion(planeId, planeRef) {
    let xDir, yDir, zDir;
    if (planeId === 'CUSTOM' && planeRef && planeRef.xDir && planeRef.normal) {
        xDir = new THREE.Vector3(...planeRef.xDir);
        zDir = new THREE.Vector3(...planeRef.normal);
        yDir = new THREE.Vector3().crossVectors(zDir, xDir).normalize();
    } else if (planeId === 'XZ') {
        xDir = new THREE.Vector3(1, 0, 0);
        yDir = new THREE.Vector3(0, 0, 1);
        zDir = new THREE.Vector3(0, -1, 0);
    } else if (planeId === 'YZ') {
        xDir = new THREE.Vector3(0, 1, 0);
        yDir = new THREE.Vector3(0, 0, 1);
        zDir = new THREE.Vector3(1, 0, 0);
    } else {
        xDir = new THREE.Vector3(1, 0, 0);
        yDir = new THREE.Vector3(0, 1, 0);
        zDir = new THREE.Vector3(0, 0, 1);
    }
    const m = new THREE.Matrix4().makeBasis(xDir, yDir, zDir);
    const q = new THREE.Quaternion().setFromRotationMatrix(m);
    return q;
}


/** Смещение центра plane'а в world-координатах для плоскости с offset.
 *  XY на Z=10 → world (0,0,10). XZ на Y=-5 → world (0,5,0).
 *  (zDir XZ = (0,-1,0), значит offset=−5 даёт world.y = +5) */
function planeWorldPosition(planeId, offset, planeRef) {
    if (planeId === 'CUSTOM' && planeRef && planeRef.origin) {
        return [...planeRef.origin];
    }
    if (!offset) return [0, 0, 0];
    if (planeId === 'XZ') return [0, -offset, 0];
    if (planeId === 'YZ') return [offset, 0, 0];
    return [0, 0, offset];
}


// === Сетка эскиза (видна только когда mode === 'sketch') ===
const SketchGrid = ({ size = 800, step = 10, majorStep = 50 }) => {
    const geometry = useMemo(() => {
        const positions = [];
        const colors = [];
        const minorColor = [0.78, 0.82, 0.88];
        const majorColor = [0.62, 0.68, 0.78];
        const axisX = [0.85, 0.20, 0.27];
        const axisY = [0.00, 0.62, 0.50];
        for (let v = -size; v <= size; v += step) {
            const isMajor = v % majorStep === 0;
            const isAxis = v === 0;
            const c = isAxis ? null : isMajor ? majorColor : minorColor;
            positions.push(v, -size, 0, v, size, 0);
            const cv = isAxis ? axisY : c;
            colors.push(...cv, ...cv);
            positions.push(-size, v, 0, size, v, 0);
            const ch = isAxis ? axisX : c;
            colors.push(...ch, ...ch);
        }
        const g = new THREE.BufferGeometry();
        g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3));
        g.setAttribute('color', new THREE.BufferAttribute(new Float32Array(colors), 3));
        return g;
    }, [size, step, majorStep]);
    return (
        <lineSegments geometry={geometry}>
            <lineBasicMaterial vertexColors transparent opacity={0.6}/>
        </lineSegments>
    );
};


// === Один примитив ===
const Primitive = ({ primitive, allPrimitives, interactive, onSelectId }) => {
    const setSelection = useCadStore((s) => s.setSelection);
    const selection = useCadStore((s) => s.selection);
    const selected = selection.kind === 'sketch_primitive' && selection.ids.includes(primitive.id);

    const handleClick = useCallback((e) => {
        if (!interactive) return;
        e.stopPropagation();
        if (onSelectId) onSelectId(primitive.id, e.shiftKey);
        else setSelection('sketch_primitive', [primitive.id]);
    }, [interactive, onSelectId, primitive.id, setSelection]);

    if (primitive.kind === 'line') {
        return (
            <LineRender
                primitive={primitive}
                allPrimitives={allPrimitives}
                selected={selected}
                interactive={interactive}
                onClick={handleClick}
            />
        );
    }
    if (primitive.kind === 'circle') {
        return (
            <CircleRender
                primitive={primitive}
                selected={selected}
                interactive={interactive}
                onClick={handleClick}
            />
        );
    }
    if (primitive.kind === 'arc') {
        return (
            <ArcRender
                primitive={primitive}
                allPrimitives={allPrimitives}
                selected={selected}
                onClick={handleClick}
            />
        );
    }
    if (primitive.kind === 'point') {
        // Выделение (оранжевый) важнее; зафиксированная точка — зелёная.
        const color = selected ? '#ff8800'
            : (primitive.fixed ? '#2aa84a' : '#cc3344');
        // Чуть крупнее когда interactive (за неё можно тащить),
        // совсем мелкая в pasive-режиме.
        const radius = interactive ? 0.7 : 0.4;
        // userData.pointId — DOM-обработчик в ActiveSketch использует
        // raycast чтобы понять что клик попал в точку (без зависимости
        // от r3f-event-системы).
        return (
            <mesh
                position={[primitive.x, primitive.y, 0.1]}
                userData={{ sketchPointId: primitive.id }}
                onClick={handleClick}
            >
                <sphereGeometry args={[radius, 12, 12]}/>
                <meshBasicMaterial color={color}/>
            </mesh>
        );
    }
    return null;
};


// Сегмент линии — отдельный компонент чтобы useMemo с зависимостями
// от координат точек гарантировал пересоздание geometry при drag.
const LineRender = ({ primitive, allPrimitives, selected, interactive, onClick }) => {
    const p1 = allPrimitives.find((p) => p.id === primitive.p1);
    const p2 = allPrimitives.find((p) => p.id === primitive.p2);
    const isAux = !!primitive.construction;
    const geometry = useMemo(() => {
        if (!p1 || !p2) return null;
        const g = new THREE.BufferGeometry();
        if (isAux) {
            /*
             * Пунктир строим САМИ — набором коротких отрезков.
             *
             * 🔴 lineDashedMaterial требует computeLineDistances(), а он есть
             * только у объекта Line, не у BufferGeometry. Вызов на геометрии
             * роняет весь редактор («computeLineDistances is not a function»),
             * что я и получил. Ручной пунктир от этого не зависит вовсе.
             */
            const dx = p2.x - p1.x;
            const dy = p2.y - p1.y;
            const len = Math.hypot(dx, dy) || 1;
            const step = 3.5;                 // штрих + пробел
            const n = Math.max(1, Math.floor(len / step));
            const pts = [];
            for (let i = 0; i < n; i++) {
                const t0 = (i * step) / len;
                const t1 = Math.min(1, (i * step + 2) / len);   // штрих 2 мм
                pts.push(
                    p1.x + dx * t0, p1.y + dy * t0, 0.05,
                    p1.x + dx * t1, p1.y + dy * t1, 0.05,
                );
            }
            g.setAttribute('position',
                new THREE.BufferAttribute(new Float32Array(pts), 3));
        } else {
            const positions = new Float32Array([p1.x, p1.y, 0.05, p2.x, p2.y, 0.05]);
            g.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        }
        return g;
    }, [p1?.x, p1?.y, p2?.x, p2?.y, isAux]);

    if (!p1 || !p2 || !geometry) return null;
    /*
     * Вспомогательная линия рисуется пунктиром и бледнее: она опора для
     * построений, а не часть контура. Без визуального отличия человек не
     * поймёт, почему одна линия попадает в выдавливание, а другая нет.
     */
    const color = selected ? '#ff8800' : (isAux ? '#8899bb' : '#1a4080');
    const dx = p2.x - p1.x, dy = p2.y - p1.y;
    const length = Math.sqrt(dx * dx + dy * dy);
    const angle = Math.atan2(dy, dx);
    const cx = (p1.x + p2.x) / 2, cy = (p1.y + p2.y) / 2;
    return (
        <group>
            {isAux ? (
                <lineSegments geometry={geometry}>
                    <lineBasicMaterial color={color} linewidth={1}/>
                </lineSegments>
            ) : (
                <line geometry={geometry}>
                    <lineBasicMaterial color={color} linewidth={2}/>
                </line>
            )}
            {interactive && (
                <mesh
                    position={[cx, cy, 0.04]}
                    rotation={[0, 0, angle]}
                    userData={{ sketchPrimitiveId: primitive.id }}
                    onClick={onClick}
                >
                    <planeGeometry args={[length, 4]}/>
                    <meshBasicMaterial transparent opacity={0}/>
                </mesh>
            )}
        </group>
    );
};

const CircleRender = ({ primitive, selected, interactive, onClick }) => {
    const geometry = useMemo(() => {
        const segments = 64;
        const positions = new Float32Array((segments + 1) * 3);
        for (let i = 0; i <= segments; i++) {
            const t = (i / segments) * Math.PI * 2;
            positions[i * 3] = primitive.cx + Math.cos(t) * primitive.r;
            positions[i * 3 + 1] = primitive.cy + Math.sin(t) * primitive.r;
            positions[i * 3 + 2] = 0.05;
        }
        const g = new THREE.BufferGeometry();
        g.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        return g;
    }, [primitive.cx, primitive.cy, primitive.r]);

    const color = selected ? '#ff8800'
        : (primitive.fixed ? '#2aa84a' : '#1a4080');
    return (
        <group>
            <line geometry={geometry}>
                <lineBasicMaterial color={color} linewidth={2}/>
            </line>
            {interactive && (
                // Невидимое кольцо-хитбокс — по тонкой линии-окружности
                // мышью не попасть. ringGeometry даёт кликабельную зону.
                <mesh
                    position={[primitive.cx, primitive.cy, 0.04]}
                    userData={{ sketchPrimitiveId: primitive.id }}
                    onClick={onClick}
                >
                    <ringGeometry args={[
                        Math.max(0, primitive.r - 3),
                        primitive.r + 3, 48,
                    ]}/>
                    <meshBasicMaterial transparent opacity={0}/>
                </mesh>
            )}
        </group>
    );
};


// Хелпер: построить точки дуги от угла a0 к a1.
// ccw — направление обхода (true = против часовой). Если ccw не задан
// (undefined) — берём кратчайшую дугу (старое поведение, для совместимости
// со старыми дугами без поля ccw).
function arcPositions(cx, cy, r, a0, a1, ccw, z = 0.05, segments = 48) {
    let span = a1 - a0;
    if (ccw === true) {
        while (span <= 0) span += Math.PI * 2;
        while (span > Math.PI * 2) span -= Math.PI * 2;
    } else if (ccw === false) {
        while (span >= 0) span -= Math.PI * 2;
        while (span < -Math.PI * 2) span += Math.PI * 2;
    } else {
        // кратчайшая дуга
        while (span <= -Math.PI) span += Math.PI * 2;
        while (span > Math.PI) span -= Math.PI * 2;
    }
    const positions = new Float32Array((segments + 1) * 3);
    for (let i = 0; i <= segments; i++) {
        const t = a0 + span * (i / segments);
        positions[i * 3] = cx + Math.cos(t) * r;
        positions[i * 3 + 1] = cy + Math.sin(t) * r;
        positions[i * 3 + 2] = z;
    }
    return positions;
}

// Дуга эскиза. Формат примитива:
//   { kind:'arc', id, p1, p2, cx, cy }
//   p1/p2 — id точек-концов; cx/cy — центр.
// Радиус и углы вычисляются из координат точек и центра.
const ArcRender = ({ primitive, allPrimitives, selected, onClick }) => {
    const p1 = allPrimitives.find((p) => p.id === primitive.p1);
    const p2 = allPrimitives.find((p) => p.id === primitive.p2);
    const geometry = useMemo(() => {
        if (!p1 || !p2) return null;
        const { cx, cy } = primitive;
        const r = Math.hypot(p1.x - cx, p1.y - cy);
        const a0 = Math.atan2(p1.y - cy, p1.x - cx);
        const a1 = Math.atan2(p2.y - cy, p2.x - cx);
        // ccw — направление обхода p1→p2 (см. формат примитива дуги).
        const positions = arcPositions(cx, cy, r, a0, a1, primitive.ccw);
        const g = new THREE.BufferGeometry();
        g.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        return g;
    }, [primitive.cx, primitive.cy, primitive.ccw, p1?.x, p1?.y, p2?.x, p2?.y]);

    if (!geometry) return null;
    const color = selected ? '#ff8800' : '#1a4080';
    return (
        <line geometry={geometry} onClick={onClick}>
            <lineBasicMaterial color={color} linewidth={2}/>
        </line>
    );
};


// === Заливка профиля (наведение/выбор в 3D — как в Fusion 360) ===
// idle — почти невидимо; hover — светло-голубой; selected — насыщенный синий
// (Fusion сменил selected с оранжевого на синий ради доступности).
const ProfileFill = ({ profile, sketch, hovered, selected, onPointerDown, zBias = 0 }) => {
    const COLOR_IDLE   = '#5a90d0';
    const COLOR_HOVER  = '#66b3ff'; // светло-голубой (Fusion hover)
    const COLOR_PICKED = '#2b8cff'; // насыщенный синий (Fusion selected)

    let fillColor = COLOR_IDLE;
    let opacity = 0.06;             // idle — еле заметная заливка, но кликабельна
    if (hovered)  { fillColor = COLOR_HOVER;  opacity = 0.25; }
    if (selected) { fillColor = COLOR_PICKED; opacity = 0.45; }

    if (profile.kind === 'circle') {
        return (
            <mesh
                position={[profile.cx, profile.cy, 0.02 + zBias]}
                onPointerOver={(e) => { e.stopPropagation(); onPointerDown?.('hover', profile.id); }}
                onPointerOut={(e)  => { e.stopPropagation(); onPointerDown?.('out', profile.id); }}
                onClick={(e) => { e.stopPropagation(); onPointerDown?.('click', profile.id, e.nativeEvent); }}
                onContextMenu={(e) => { e.stopPropagation(); e.nativeEvent?.preventDefault?.(); onPointerDown?.('menu', profile.id, e.nativeEvent); }}
            >
                <circleGeometry args={[profile.r, 64]}/>
                <meshBasicMaterial color={fillColor} transparent opacity={opacity} depthWrite={false} side={THREE.DoubleSide}/>
            </mesh>
        );
    }
    if (profile.kind === 'polygon' || profile.kind === 'region') {
        const verts = profilePolygonVertices(profile, sketch);
        if (verts.length < 3) return null;
        // Через THREE.Shape чтобы корректно строить mesh из полигона
        const shape = new THREE.Shape();
        shape.moveTo(verts[0].x, verts[0].y);
        for (let i = 1; i < verts.length; i++) shape.lineTo(verts[i].x, verts[i].y);
        shape.closePath();
        const geometry = new THREE.ShapeGeometry(shape);
        // Чуть «приподнимаем» над плоскостью эскиза чтобы не было z-fight
        return (
            <mesh
                geometry={geometry}
                position={[0, 0, 0.02 + zBias]}
                onPointerOver={(e) => { e.stopPropagation(); onPointerDown?.('hover', profile.id); }}
                onPointerOut={(e)  => { e.stopPropagation(); onPointerDown?.('out', profile.id); }}
                onClick={(e) => { e.stopPropagation(); onPointerDown?.('click', profile.id, e.nativeEvent); }}
                onContextMenu={(e) => { e.stopPropagation(); e.nativeEvent?.preventDefault?.(); onPointerDown?.('menu', profile.id, e.nativeEvent); }}
            >
                <meshBasicMaterial color={fillColor} transparent opacity={opacity} depthWrite={false} side={THREE.DoubleSide}/>
            </mesh>
        );
    }
    return null;
};


// === Группа всех профилей одного эскиза ===
const SketchProfiles = ({ sketch, isPickerActive }) => {
    const selection = useCadStore((s) => s.selection);
    const setSelection = useCadStore((s) => s.setSelection);
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const updatePendingFeature = useCadStore((s) => s.updatePendingFeature);
    const openProfileContextMenu = useCadStore((s) => s.openProfileContextMenu);
    const [hoveredId, setHoveredId] = useState(null);

    const profiles = useMemo(() => findProfiles(sketch), [sketch]);

    /*
     * 🔴 МЕЛКИЙ ПРОФИЛЬ ПЕРЕХВАТЫВАЕТ КЛИК У КРУПНОГО.
     *
     * Профили — плоские меши на ОДНОЙ высоте, и каждый гасит событие через
     * stopPropagation. Когда один профиль лежит внутри другого (отверстие
     * внутри пластины; круг внутри полосы, отрезанной линией разметки),
     * выигрывал произвольный из них — на практике тот, что раньше в списке,
     * то есть обычно КРУПНЫЙ. Человек целился в отверстие Ø8, а выбиралась
     * полоса 975 мм², и выдавливалась она.
     *
     * Раскладываем по высоте: чем меньше площадь, тем выше лежит профиль.
     * Тогда попадание в мелкий контур всегда выигрывает, а щёлкнуть по
     * большому по-прежнему можно — там, где мелкого под курсором нет.
     * Шаг крошечный (0.001 мм на ранг), на вид не влияет.
     */
    const ordered = useMemo(() => {
        const withArea = profiles.map((p) => ({
            p,
            a: Math.abs(profileArea(p, sketch)) || 0,
        }));
        // Самый крупный — ранг 0 (ниже всех), самый мелкий — выше всех.
        withArea.sort((x, y) => y.a - x.a);
        return withArea.map((it, i) => ({ profile: it.p, zBias: i * 0.001 }));
    }, [profiles, sketch]);

    // Профили видимы/интерактивны в view3d всегда (isPickerActive) либо когда
    // конкретный профиль выбран.
    const visible = isPickerActive || (selection.kind === 'profile' && selection.ids.length > 0);
    if (!visible) return null;

    // Операции, которые выбирают профиль эскиза как основу.
    const opNeedsProfile = pendingFeature &&
        ['extrude', 'revolve', 'sweep', 'loft'].includes(pendingFeature.kind);

    const handle = (action, profileId, ev) => {
        if (action === 'hover') { setHoveredId(profileId); return; }
        if (action === 'out') { setHoveredId((cur) => (cur === profileId ? null : cur)); return; }

        const refId = `${sketch.id}/${profileId}`;
        // Ctrl/Shift/Cmd — множественный выбор профилей (toggle в наборе).
        const multi = ev?.ctrlKey || ev?.shiftKey || ev?.metaKey;
        const cur = useCadStore.getState().selection;
        const curIds = cur.kind === 'profile' ? cur.ids : [];
        // Правый клик (menu) по профилю, который УЖЕ в наборе — сохраняем весь
        // набор (не сбрасываем на один), чтобы меню применилось ко всем.
        const keepSet = action === 'menu' && curIds.includes(refId);
        const nextIds = (multi || keepSet)
            ? (multi
                ? (curIds.includes(refId) ? curIds.filter((id) => id !== refId) : [...curIds, refId])
                : curIds)
            : [refId];

        // Идёт операция, которой нужен профиль — отдаём выбор ей.
        if (opNeedsProfile) {
            setSelection('profile', nextIds);
            if (pendingFeature.kind === 'extrude') {
                updatePendingFeature({ profileRefs: nextIds });
            }
            return;
        }
        // Обычный режим (нет операции): выделяем набор; меню действий открываем
        // (Выдавить/Вращение/…). При Ctrl-накоплении меню НЕ открываем на каждый
        // клик — только на обычный клик или правый (menu-действие).
        setSelection('profile', nextIds);
        if (!pendingFeature && (!multi || action === 'menu') && nextIds.length > 0) {
            const x = ev?.clientX ?? window.innerWidth / 2;
            const y = ev?.clientY ?? window.innerHeight / 2;
            // Меню несёт ВСЕ выбранные профили (операция применится ко всем).
            openProfileContextMenu(nextIds, x, y);
        }
    };



    return (
        <group>
            {ordered.map(({ profile: p, zBias }) => {
                const refId = `${sketch.id}/${p.id}`;
                const isSelected = selection.kind === 'profile' && selection.ids.includes(refId);
                return (
                    <ProfileFill
                        key={p.id}
                        profile={p}
                        sketch={sketch}
                        hovered={hoveredId === p.id}
                        selected={isSelected}
                        onPointerDown={handle}
                        zBias={zBias}
                    />
                );
            })}
        </group>
    );
};


// === Drag-preview ===
// Чтобы r3f гарантированно перерисовывал линию при изменении координат,
// строим THREE.BufferGeometry вручную через useMemo с зависимостями от
// координат, и передаём через `<line geometry={...}>`. Это надёжнее чем
// объявлять <bufferAttribute array={...}/> декларативно — там reconciler
// иногда не подхватывает обновление массива.
const DrawPreview = ({ tool, start, current, polygonSides = 6 }) => {
    const COLOR = '#dca838';

    const geometry = useMemo(() => {
        if (!start || !current || !tool) return null;
        let positions;
        /*
         * 🔴 constructionLine рисуется так же, как обычная линия.
         *
         * Раньше здесь стояло только 'line', и у вспомогательной линии не
         * было ПРЕВЬЮ: между первым и вторым кликом на экране ничего не
         * тянулось. Со стороны это выглядело так, будто линия ставится сама
         * непонятно куда — хотя механика кликов у неё та же самая.
         */
        if (tool === 'line' || tool === 'constructionLine') {
            positions = new Float32Array([
                start.x, start.y, 0.06,
                current.x, current.y, 0.06,
            ]);
        } else if (tool === 'rectangle') {
            const x1 = start.x, y1 = start.y, x2 = current.x, y2 = current.y;
            positions = new Float32Array([
                x1, y1, 0.06,  x2, y1, 0.06,
                x2, y1, 0.06,  x2, y2, 0.06,
                x2, y2, 0.06,  x1, y2, 0.06,
                x1, y2, 0.06,  x1, y1, 0.06,
            ]);
        } else if (tool === 'circle') {
            const dx = current.x - start.x, dy = current.y - start.y;
            const r = Math.sqrt(dx * dx + dy * dy);
            const segments = 64;
            positions = new Float32Array((segments + 1) * 3);
            for (let i = 0; i <= segments; i++) {
                const t = (i / segments) * Math.PI * 2;
                positions[i * 3] = start.x + Math.cos(t) * r;
                positions[i * 3 + 1] = start.y + Math.sin(t) * r;
                positions[i * 3 + 2] = 0.06;
            }
        } else if (tool === 'ellipse') {
            // Превью эллипса: полуоси задаются смещением курсора от центра.
            const rx = Math.abs(current.x - start.x), ry = Math.abs(current.y - start.y);
            const segments = 64;
            positions = new Float32Array((segments + 1) * 3);
            for (let i = 0; i <= segments; i++) {
                const t = (i / segments) * Math.PI * 2;
                positions[i * 3] = start.x + Math.cos(t) * rx;
                positions[i * 3 + 1] = start.y + Math.sin(t) * ry;
                positions[i * 3 + 2] = 0.06;
            }
        } else if (tool === 'slot') {
            // Превью паза: два полукруга по концам оси + прямые борта.
            const dx = current.x - start.x, dy = current.y - start.y;
            const len = Math.hypot(dx, dy) || 1;
            const w = Math.max(1, len / 3) / 2;
            const ux = dx / len, uy = dy / len, nx = -uy, ny = ux;
            const N = 16, pts = [];
            for (let i = 0; i <= N; i++) {
                const a = -Math.PI / 2 + (i / N) * Math.PI;
                pts.push([current.x + (ux * Math.cos(a) + nx * Math.sin(a)) * w,
                          current.y + (uy * Math.cos(a) + ny * Math.sin(a)) * w]);
            }
            for (let i = 0; i <= N; i++) {
                const a = Math.PI / 2 + (i / N) * Math.PI;
                pts.push([start.x + (ux * Math.cos(a) + nx * Math.sin(a)) * w,
                          start.y + (uy * Math.cos(a) + ny * Math.sin(a)) * w]);
            }
            pts.push(pts[0]);
            positions = new Float32Array(pts.length * 3);
            pts.forEach((p, i) => {
                positions[i * 3] = p[0]; positions[i * 3 + 1] = p[1]; positions[i * 3 + 2] = 0.06;
            });
        } else if (tool === 'polygon') {
            // Превью N-угольника: замкнутая ломаная по вершинам.
            const dx = current.x - start.x, dy = current.y - start.y;
            const r = Math.sqrt(dx * dx + dy * dy);
            const n = Math.max(3, Math.min(64, polygonSides || 6));
            const a0 = Math.atan2(dy, dx);
            positions = new Float32Array((n + 1) * 3);
            for (let i = 0; i <= n; i++) {
                const a = a0 + (i / n) * Math.PI * 2;
                positions[i * 3] = start.x + Math.cos(a) * r;
                positions[i * 3 + 1] = start.y + Math.sin(a) * r;
                positions[i * 3 + 2] = 0.06;
            }
        } else {
            return null;
        }
        const g = new THREE.BufferGeometry();
        g.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        return g;
    }, [tool, start?.x, start?.y, current?.x, current?.y, polygonSides]);

    if (!geometry) return null;

    if (tool === 'rectangle') {
        return (
            <lineSegments geometry={geometry}>
                <lineBasicMaterial color={COLOR}/>
            </lineSegments>
        );
    }
    return (
        <line geometry={geometry}>
            <lineBasicMaterial color={COLOR}/>
        </line>
    );
};


// Превью дуги во время трёхкликового рисования.
//   draft = {center} | {center, startPt}; hover — позиция мыши.
const ArcDraftPreview = ({ draft, hover }) => {
    const COLOR = '#dca838';
    const geometry = useMemo(() => {
        if (!draft || !hover) return null;
        const { center, startPt } = draft;
        if (!startPt) {
            // Этап «центр поставлен» — preview радиуса как отрезок
            // центр→курсор.
            const pos = new Float32Array([
                center.x, center.y, 0.07,
                hover.x, hover.y, 0.07,
            ]);
            const g = new THREE.BufferGeometry();
            g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
            return { g, type: 'line' };
        }
        // Этап «начало поставлено» — preview самой дуги. Направление —
        // куда пользователь ведёт курсор (кратчайшая дуга к hover).
        const r = Math.hypot(startPt.x - center.x, startPt.y - center.y);
        const a0 = Math.atan2(startPt.y - center.y, startPt.x - center.x);
        const a1 = Math.atan2(hover.y - center.y, hover.x - center.x);
        const pos = arcPositions(center.x, center.y, r, a0, a1, undefined, 0.07);
        const g = new THREE.BufferGeometry();
        g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
        return { g, type: 'arc' };
    }, [draft, hover]);

    if (!draft) return null;
    return (
        <>
            {/* маркер центра */}
            <mesh position={[draft.center.x, draft.center.y, 0.08]}>
                <sphereGeometry args={[0.8, 12, 12]}/>
                <meshBasicMaterial color={COLOR}/>
            </mesh>
            {geometry && (
                <line geometry={geometry.g}>
                    <lineBasicMaterial color={COLOR}/>
                </line>
            )}
        </>
    );
};


// ===== Поле ввода размера (инструмент «Размер») =====
// Привязано к точке на эскизе (середина линии / центр окружности)
// через drei <Html>. Пользователь вводит значение → applyDimension
// создаёт размерное ограничение и решает эскиз.
const DimensionInput = ({ draft, onApply, onCancel }) => {
    const [text, setText] = useState(String(draft.current ?? ''));
    const inputRef = useRef(null);
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, []);
    const commit = () => {
        const v = parseFloat(String(text).replace(',', '.'));
        if (v > 0) onApply(v);
        else onCancel();
    };
    return (
        <Html position={[draft.x, draft.y, 0.2]} center zIndexRange={[100, 0]}>
            <div
                style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    background: 'rgba(28,32,40,0.97)',
                    border: '1px solid #e8b73a', borderRadius: 6,
                    padding: '4px 6px',
                    boxShadow: '0 4px 14px rgba(0,0,0,0.5)',
                }}
                onPointerDown={(e) => e.stopPropagation()}
            >
                <span style={{ color: '#e8b73a', fontSize: 11, fontWeight: 700 }}>
                    {draft.kind === 'diameter' ? '⌀' : 'L'}
                </span>
                <input
                    ref={inputRef}
                    value={text}
                    onChange={(e) => setText(
                        e.target.value.replace(/[^\d.,]/g, ''))}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') commit();
                        if (e.key === 'Escape') onCancel();
                    }}
                    style={{
                        width: 56, background: '#11151c',
                        border: '1px solid #3a4252', borderRadius: 4,
                        color: '#fff', fontSize: 13, fontWeight: 700,
                        padding: '3px 5px', textAlign: 'center',
                    }}
                />
                <span style={{ color: '#8b95a5', fontSize: 10 }}>мм</span>
                <button
                    onClick={commit}
                    style={{
                        width: 22, height: 22, borderRadius: 4, border: 'none',
                        background: '#e8b73a', color: '#1a1300',
                        fontSize: 13, fontWeight: 800, cursor: 'pointer',
                    }}
                >✓</button>
            </div>
        </Html>
    );
};


// ===== Подписи проставленных размеров =====
// Для каждого размерного ограничения (distance/diameter) рисует
// значение рядом с объектом. Клик по подписи — редактирование.
const DimensionLabels = ({ sketch, onEdit }) => {
    const constraints = sketch.constraints || [];
    const prims = sketch.primitives || [];
    const dims = constraints.filter(
        (c) => c.kind === 'distance' || c.kind === 'diameter');
    if (dims.length === 0) return null;
    return (
        <>
            {dims.map((c) => {
                let x, y, text;
                if (c.kind === 'distance') {
                    const p1 = prims.find((p) => p.id === c.p1Id);
                    const p2 = prims.find((p) => p.id === c.p2Id);
                    if (!p1 || !p2) return null;
                    x = (p1.x + p2.x) / 2;
                    y = (p1.y + p2.y) / 2;
                    text = `${c.value} мм`;
                } else {
                    const circ = prims.find((p) => p.id === c.circleId);
                    if (!circ) return null;
                    x = circ.cx;
                    y = circ.cy;
                    text = `⌀ ${c.value}`;
                }
                return (
                    <Html
                        key={c.id}
                        position={[x, y, 0.15]}
                        center
                        zIndexRange={[60, 0]}
                    >
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                onEdit?.(c);
                            }}
                            style={{
                                background: 'rgba(20,24,30,0.85)',
                                border: '1px solid #4a5568',
                                borderRadius: 4, padding: '1px 6px',
                                color: '#e8b73a', fontSize: 11,
                                fontWeight: 700, whiteSpace: 'nowrap',
                                cursor: 'pointer', userSelect: 'none',
                            }}
                        >
                            {text}
                        </div>
                    </Html>
                );
            })}
        </>
    );
};


// ===== Конвертация экранных координат в плоские координаты эскиза =====
//
// Идея: пускаем луч из камеры через клик. Считаем пересечение с плоскостью
// эскиза (которая уже позиционирована в group с нужным rotation).
// Чтобы получить «локальные» координаты, делаем `groupMatrix.invert() * worldPoint`.
function screenToSketchPlane(camera, domElement, clientX, clientY, group, opts = {}) {
    const rect = domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    const ray = new THREE.Raycaster();
    ray.setFromCamera(ndc, camera);

    const planeNormalLocal = new THREE.Vector3(0, 0, 1);
    const worldQuat = new THREE.Quaternion();
    group.getWorldQuaternion(worldQuat);
    const worldNormal = planeNormalLocal.clone().applyQuaternion(worldQuat);

    const worldOrigin = new THREE.Vector3();
    group.getWorldPosition(worldOrigin);

    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(worldNormal, worldOrigin);
    const intersection = new THREE.Vector3();
    if (!ray.ray.intersectPlane(plane, intersection)) return null;

    const inv = new THREE.Matrix4();
    group.updateMatrixWorld(true);
    inv.copy(group.matrixWorld).invert();
    intersection.applyMatrix4(inv);

    let x = intersection.x;
    let y = intersection.y;
    let snapped = null;

    // Snap к якорным точкам (углы грани): если рядом — магнитим курсор.
    // threshold переводим из пикселей в локальные единицы плоскости через
    // pixelsToWorld коэффициент camera.
    if (opts.anchors && opts.anchors.length > 0) {
        const thresholdPx = opts.snapPx || 12;
        // Считаем сколько мм на плоскости приходится на 1 px экрана.
        // Берём worldOrigin → проецируем в screen вместе с offset 1px,
        // обратно ловим разницу — но проще: используем расстояние до камеры.
        const dist = camera.position.distanceTo(worldOrigin);
        // FOV camera у Three.js в радианах. Высота viewport в world units =
        // 2 * dist * tan(fov/2). Делим на rect.height чтобы получить mm/px.
        const fovRad = (camera.fov || 35) * Math.PI / 180;
        const worldPerPx = (2 * dist * Math.tan(fovRad / 2)) / rect.height;
        const thresholdWorld = thresholdPx * worldPerPx;
        // Поиск лучшего якоря: в пределах threshold выигрывает МЕНЬШИЙ priority
        // (endpoint=1 бьёт midpoint/center=2), при равном — ближайший.
        let best = null;
        for (const a of opts.anchors) {
            const dx = a.x - x;
            const dy = a.y - y;
            const d = Math.sqrt(dx * dx + dy * dy);
            if (d >= thresholdWorld) continue;
            const pr = a.priority || 1;
            if (!best || pr < best.pr || (pr === best.pr && d < best.d)) {
                best = { a, d, pr };
            }
        }
        if (best) {
            x = best.a.x;
            y = best.a.y;
            // pointId есть только у якорей-точек эскиза — он нужен,
            // чтобы новая фигура переиспользовала точку (замыкание контура).
            // type — для маркера привязки (endpoint/midpoint/center).
            snapped = { x: best.a.x, y: best.a.y,
                        pointId: best.a.pointId || null,
                        type: best.a.type || 'endpoint' };
        }
    }

    /*
     * Выравнивание по осям — как в Fusion.
     *
     * Пока ведёшь вторую точку линии, курсор «прилипает» к горизонтали или
     * вертикали от первой: рука почти никогда не попадает в ровные 0° и 90°,
     * а именно они нужны в 99 % случаев. Без этого каждую линию приходится
     * доводить ограничением вручную — главная причина, почему в самодельных
     * редакторах рисовать медленнее, чем в Fusion.
     *
     * Привязка к точке (выше) важнее: если курсор уже сел на вершину, ось не
     * трогаем — иначе фигура не замкнётся.
     */
    if (!snapped && opts.axisFrom) {
        const dist = camera.position.distanceTo(worldOrigin);
        const fovRad = (camera.fov || 35) * Math.PI / 180;
        const worldPerPx = (2 * dist * Math.tan(fovRad / 2)) / rect.height;
        const tol = (opts.axisPx || 8) * worldPerPx;
        const dx = x - opts.axisFrom.x;
        const dy = y - opts.axisFrom.y;
        // Тянем вдоль оси, только если по ней уже отошли заметно дальше,
        // чем поперёк: иначе у самой первой точки прыгало бы между осями.
        if (Math.abs(dy) < tol && Math.abs(dx) > tol) {
            y = opts.axisFrom.y;
            snapped = { x, y, pointId: null, type: 'horizontal' };
        } else if (Math.abs(dx) < tol && Math.abs(dy) > tol) {
            x = opts.axisFrom.x;
            snapped = { x, y, pointId: null, type: 'vertical' };
        }
    }

    return { x, y, snapped };
}


// ===== Маркеры привязок (snap) =====
// Постоянно — лёгкие endpoint-точки граней (ориентир). При захвате (activeSnap)
// — крупный маркер ПО ТИПУ, как в Fusion:
//   endpoint → квадрат, midpoint → ТРЕУГОЛЬНИК, center → круг.
// Цвет активного — бирюзово-голубой (акцент привязки).
const SnapMarkers = ({ anchors, activeSnap }) => {
    return (
        <group>
            {/* Лёгкие ориентиры — только endpoint-якоря граней (без pointId). */}
            {(anchors || []).filter((a) => a.type === 'endpoint').map((a, i) => (
                <mesh key={`e${i}`} position={[a.x, a.y, 0.05]} renderOrder={5}>
                    <circleGeometry args={[0.9, 12]}/>
                    <meshBasicMaterial color="#3a78ff" transparent opacity={0.5}
                        depthTest={false} depthWrite={false}/>
                </mesh>
            ))}
            {/* Активный маркер захвата — форма по типу привязки. */}
            {activeSnap && <SnapGlyph snap={activeSnap}/>}
        </group>
    );
};

// Контурные геометрии маркеров привязки (единичный размер, потом масштаб).
// Как в Fusion — тонкие КОНТУРЫ, не залитые. Собраны из точек LineLoop.
function makeLoopGeom(pts2d) {
    const g = new THREE.BufferGeometry();
    const arr = new Float32Array(pts2d.length * 3);
    for (let i = 0; i < pts2d.length; i++) {
        arr[i * 3] = pts2d[i][0]; arr[i * 3 + 1] = pts2d[i][1]; arr[i * 3 + 2] = 0;
    }
    g.setAttribute('position', new THREE.BufferAttribute(arr, 3));
    return g;
}
// Треугольник вершиной вверх (единичный масштаб ~1).
const GLYPH_TRI = makeLoopGeom([[0, 1], [-0.9, -0.7], [0.9, -0.7]]);
// Квадрат.
const GLYPH_SQ = makeLoopGeom([[-0.8, -0.8], [0.8, -0.8], [0.8, 0.8], [-0.8, 0.8]]);
// Значки выравнивания по осям: короткое тире (горизонталь) и палочка
// (вертикаль) — как в Fusion, чтобы было видно, ПОЧЕМУ курсор прилип.
const GLYPH_HORZ = makeLoopGeom([[-1, -0.12], [1, -0.12], [1, 0.12], [-1, 0.12]]);
const GLYPH_VERT = makeLoopGeom([[-0.12, -1], [0.12, -1], [0.12, 1], [-0.12, 1]]);
// Кольцо (окружность контуром).
const GLYPH_CIRC = (() => {
    const pts = [];
    for (let i = 0; i < 20; i++) {
        const a = (i / 20) * Math.PI * 2;
        pts.push([Math.cos(a), Math.sin(a)]);
    }
    return makeLoopGeom(pts);
})();

// Глиф активной привязки — контурный, ФИКСИРОВАННОГО ЭКРАННОГО размера (~10px),
// цвет циан (как inference-маркеры Fusion). Масштаб пересчитывается каждый кадр
// по расстоянию до камеры, чтобы маркер не «раздувался» при отдалении.
const SnapGlyph = ({ snap }) => {
    const ref = useRef();
    const { camera, size } = useThree();
    const TARGET_PX = 11; // высота маркера на экране
    useFrame(() => {
        if (!ref.current) return;
        // мм на 1 px экрана в точке маркера
        const p = new THREE.Vector3(snap.x, snap.y, 0);
        ref.current.parent?.localToWorld(p);
        const dist = camera.position.distanceTo(p);
        const fovRad = (camera.fov || 35) * Math.PI / 180;
        const worldPerPx = (2 * dist * Math.tan(fovRad / 2)) / size.height;
        ref.current.scale.setScalar(worldPerPx * TARGET_PX);
    });
    const geom = snap.type === 'midpoint' ? GLYPH_TRI
        : snap.type === 'center' ? GLYPH_CIRC
        : snap.type === 'horizontal' ? GLYPH_HORZ
        : snap.type === 'vertical' ? GLYPH_VERT : GLYPH_SQ;
    return (
        <lineLoop ref={ref} geometry={geom} position={[snap.x, snap.y, 0.08]} renderOrder={7}>
            <lineBasicMaterial color="#00b0f0" transparent opacity={0.95}
                depthTest={false} depthWrite={false}/>
        </lineLoop>
    );
};


// ===== Активный эскиз (с обработкой ввода) =====
const ActiveSketch = ({ sketch }) => {
    const { camera, gl } = useThree();
    const groupRef = useRef();

    const activeTool = useCadStore((s) => s.activeSketchTool);
    const addSketchPrimitive = useCadStore((s) => s.addSketchPrimitive);
    const updateSketchPrimitive = useCadStore((s) => s.updateSketchPrimitive);
    const removeSketchPrimitive = useCadStore((s) => s.removeSketchPrimitive);
    const updateSketch = useCadStore((s) => s.updateSketch);
    const clearSelection = useCadStore((s) => s.clearSelection);
    const selection = useCadStore((s) => s.selection);
    const setSelection = useCadStore((s) => s.setSelection);
    const pendingShape = useCadStore((s) => s.pendingShape);
    const setPendingShape = useCadStore((s) => s.setPendingShape);
    const updatePendingShapeEnd = useCadStore((s) => s.updatePendingShapeEnd);
    const updatePendingShapePhase = useCadStore((s) => s.updatePendingShapePhase);
    const drawSizeOverride = useCadStore((s) => s.drawSizeOverride);
    const setDrawSizeOverride = useCadStore((s) => s.setDrawSizeOverride);

    const draggingPointIdRef = useRef(null);
    const drawStartRef = useRef(null);
    // Состояние «клик vs drag» для точки эскиза:
    //   pointDragMovedRef — был ли реальный сдвиг мышью;
    //   pointDownPosRef   — экранная позиция в момент pointerdown;
    //   pointShiftRef     — был ли зажат Shift (для мультивыбора).
    const pointDragMovedRef = useRef(false);
    const pointDownPosRef = useRef(null);
    const pointShiftRef = useRef(false);

    // Дуга рисуется ТРЕМЯ кликами (центр → начало → конец) — обычный
    // двухточечный pendingShape для неё не подходит. arcDraft хранит
    // накопленные клики: null | {center} | {center, startPt}.
    // arcHover — текущая позиция мыши для preview между кликами.
    /*
     * Сплайн копит опорные точки по кликам, как дуга — свои три. Завершается
     * по Enter или Escape: заранее неизвестно, сколько точек нужно человеку.
     */
    const [splineDraft, setSplineDraft] = useState(null);
    const splineDraftRef = useRef(null);
    splineDraftRef.current = splineDraft;

    const [arcDraft, setArcDraft] = useState(null);
    const [arcHover, setArcHover] = useState(null);
    const arcDraftRef = useRef(null);
    arcDraftRef.current = arcDraft;

    // Сброс незавершённой дуги при смене инструмента (в т.ч. Escape).
    useEffect(() => {
        if (activeTool !== 'arc') {
            setArcDraft(null);
            setArcHover(null);
        }
    }, [activeTool]);

    // Число сторон многоугольника (из store — общее с панелью ввода).
    // Многоугольник двухточечный (центр → вершина), рисуется через
    // обычный pendingShape с tool='polygon'.
    const polygonSides = useCadStore((s) => s.polygonSides);
    const polygonSidesRef = useRef(6);
    polygonSidesRef.current = polygonSides;

    // Черновик размерного ограничения (инструмент 'dimension').
    // null | { kind:'length'|'diameter', targetId, current, x, y }
    //   kind     — что меряем (длина линии / диаметр окружности);
    //   targetId — id линии или окружности;
    //   current  — текущее значение (для подстановки в поле);
    //   x,y      — где показать поле ввода (середина линии / центр круга).
    const [dimDraft, setDimDraft] = useState(null);
    useEffect(() => {
        if (activeTool !== 'dimension') setDimDraft(null);
    }, [activeTool]);

    // Статус решателя эскиза пишем в стор (читает SketchOverlayUI для баннера):
    //   dof=0 → полностью определён; dof>0 → недоопределён; conflicts → конфликт.
    const setSketchStatus = useCadStore((s) => s.setSketchSolveStatus);

    const snap = (v) => Math.round(v);

    const lastSolveAt = useRef(0);
    const solveSketchAt = useCallback(async (pointId, x, y) => {
        if (!sketch) return;
        const solver = getSolverClient();
        if (!solver.ready) return;
        const now = performance.now();
        if (now - lastSolveAt.current < 16) return;
        lastSolveAt.current = now;
        try {
            const gcsPrims = sketchToGcsPrimitives(sketch);
            const res = await solver.solve(gcsPrims, {
                draggingId: pointId, draggingX: x, draggingY: y,
            });
            setSketchStatus({ dof: res.dof, conflicts: res.conflicts || [], failed: !!res.failed });
            // При конфликте/неудаче не применяем «поехавшую» геометрию —
            // точка остаётся где была (аудит эскизов #3).
            if (res.failed || (res.conflicts && res.conflicts.length)) return;
            const updated = applyGcsResultToSketch(sketch, res.primitives);
            updateSketch(sketch.id, { primitives: updated.primitives });
        } catch (e) {
            updateSketchPrimitive(pointId, { x, y });
        }
    }, [sketch, updateSketch, updateSketchPrimitive, setSketchStatus]);

    // Применить размер из dimDraft: создать (или обновить, если
    // редактируем существующее) размерное ограничение и решить эскиз.
    const addSketchConstraint = useCadStore((s) => s.addSketchConstraint);
    const updateSketchConstraint = useCadStore((s) => s.updateSketchConstraint);
    const applyDimension = useCallback((value) => {
        const d = dimDraft;
        if (!d || !(value > 0)) { setDimDraft(null); return; }
        if (d.editConstraintId) {
            // Редактирование существующего размера — меняем value.
            updateSketchConstraint(d.editConstraintId, { value });
        } else {
            const prims = sketch.primitives || [];
            const target = prims.find((p) => p.id === d.targetId);
            if (!target) { setDimDraft(null); return; }
            if (d.kind === 'length' && target.kind === 'line') {
                addSketchConstraint({
                    id: newId('cn'), kind: 'distance',
                    p1Id: target.p1, p2Id: target.p2, value,
                });
            } else if (d.kind === 'diameter' && target.kind === 'circle') {
                addSketchConstraint({
                    id: newId('cn'), kind: 'diameter',
                    circleId: target.id, value,
                });
            }
        }
        setDimDraft(null);
        // Решаем эскиз с новым ограничением (после применения в store).
        setTimeout(() => solveSketchConstraintsRef.current?.(), 0);
    }, [dimDraft, sketch, addSketchConstraint, updateSketchConstraint]);

    // Открыть редактирование существующего размера (клик по подписи).
    const editDimension = useCallback((constraint) => {
        const prims = sketch.primitives || [];
        if (constraint.kind === 'distance') {
            const p1 = prims.find((p) => p.id === constraint.p1Id);
            const p2 = prims.find((p) => p.id === constraint.p2Id);
            if (!p1 || !p2) return;
            setDimDraft({
                kind: 'length', editConstraintId: constraint.id,
                current: constraint.value,
                x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2,
            });
        } else if (constraint.kind === 'diameter') {
            const circ = prims.find((p) => p.id === constraint.circleId);
            if (!circ) return;
            setDimDraft({
                kind: 'diameter', editConstraintId: constraint.id,
                current: constraint.value, x: circ.cx, y: circ.cy,
            });
        }
    }, [sketch]);

    // Решить эскиз по всем ограничениям БЕЗ перетаскивания точки.
    // Вызывается после добавления/изменения размерного ограничения —
    // решатель подгоняет геометрию под новые значения.
    const solveSketchConstraints = useCallback(async () => {
        if (!sketch) return;
        const solver = getSolverClient();
        if (!solver.ready) return;
        try {
            // Берём актуальный эскиз из store (constraint уже добавлен).
            const cur = useCadStore.getState().doc.sketches
                .find((s) => s.id === sketch.id);
            if (!cur) return;
            const gcsPrims = sketchToGcsPrimitives(cur);
            const res = await solver.solve(gcsPrims, null);
            setSketchStatus({ dof: res.dof, conflicts: res.conflicts || [], failed: !!res.failed });
            // Конфликт/неудача — геометрию не трогаем, только сообщаем статусом.
            if (res.failed || (res.conflicts && res.conflicts.length)) return;
            const updated = applyGcsResultToSketch(cur, res.primitives);
            updateSketch(sketch.id, { primitives: updated.primitives });
        } catch (e) {
            console.warn('[sketch] solve constraints failed:', e);
        }
    }, [sketch, updateSketch, setSketchStatus]);

    // ref на solveSketchConstraints — чтобы applyDimension (объявлен
    // выше) мог вызвать актуальную версию через setTimeout.
    /*
     * Завершение сплайна: Enter — построить, Escape — отменить.
     *
     * Сколько опорных точек нужно, заранее неизвестно, поэтому кривая
     * строится не после N-го клика, а по явной команде — как в Fusion.
     *
     * Готовый сплайн раскладываем в ЛОМАНУЮ из линий эскиза: так он
     * участвует в контуре, ограничениях и выдавливании наравне с остальной
     * геометрией, без отдельного типа примитива в ядре.
     */
    useEffect(() => {
        // Проверять режим не нужно: этот компонент живёт только внутри
        // активного эскиза.
        const onKey = (e) => {
            const draft = splineDraftRef.current;
            if (!draft || draft.length === 0) return;
            if (e.key === 'Escape') {
                e.preventDefault();
                setSplineDraft(null);
                return;
            }
            if (e.key !== 'Enter') return;
            e.preventDefault();
            if (draft.length < 2) { setSplineDraft(null); return; }
            const poly = splineToPolyline(draft, 10, false);
            // Точки ломаной и соединяющие их линии.
            let prevId = null;
            for (const q of poly) {
                const id = newId('p');
                addSketchPrimitive({ kind: 'point', id, x: q.x, y: q.y });
                if (prevId) {
                    addSketchPrimitive({
                        kind: 'line', id: newId('l'), p1: prevId, p2: id,
                    });
                }
                prevId = id;
            }
            setSplineDraft(null);
            setTimeout(() => solveSketchConstraintsRef.current?.(), 0);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    });

    const solveSketchConstraintsRef = useRef(null);
    solveSketchConstraintsRef.current = solveSketchConstraints;

    /*
     * Статус эскиза (степени свободы) пересчитываем при ЛЮБОМ изменении
     * геометрии, а не только при перетаскивании точки и вводе размера.
     *
     * 🔴 Из-за этого значок «Недоопределён · степеней свободы: N» не появлялся
     * вовсе: нарисовал прямоугольник — решатель не вызван, статус пуст, и
     * SketchStatusBadge остаётся скрытым. А это главный показатель состояния
     * эскиза: в Fusion по нему сразу видно, зафиксирована форма или ещё нет.
     *
     * Считаем с небольшой задержкой, чтобы не дёргать решатель на каждый
     * промежуточный кадр при рисовании.
     */
    const primCount = sketch?.primitives?.length ?? 0;
    const constrCount = sketch?.constraints?.length ?? 0;
    useEffect(() => {
        if (!sketch) return undefined;
        const t = setTimeout(() => solveSketchConstraintsRef.current?.(), 180);
        return () => clearTimeout(t);
    }, [sketch, primCount, constrCount]);

    // Применение геометрического ограничения к выделенным примитивам.
    // TopBar выставляет requestConstraint {kind, ts}; ловим изменение ts.
    const requestConstraint = useCadStore((s) => s.requestConstraint);
    const setRequestConstraint = useCadStore((s) => s.setRequestConstraint);
    const lastConstraintTs = useRef(0);
    useEffect(() => {
        if (!requestConstraint || requestConstraint.ts === lastConstraintTs.current) {
            return;
        }
        lastConstraintTs.current = requestConstraint.ts;
        const kind = requestConstraint.kind;
        const ids = (selection.kind === 'sketch_primitive')
            ? selection.ids : [];
        const prims = sketch.primitives || [];

        /*
         * Зеркало и массив — не ограничения, а копирование выделенного.
         * Идут этим же каналом, потому что работают так же: по выбранным
         * примитивам, а не по клику.
         */
        /*
         * Проецирование рёбер тела в эскиз.
         *
         * Основа работы в Fusion: чтобы новая деталь совпала с уже
         * построенной, её контур не рисуют на глаз, а строят от рёбер
         * готового тела. Без этого крышка не сядет на корпус.
         *
         * Проецируем ВСЕ рёбра выбранного тела: в режиме эскиза отдельные
         * рёбра не выделяются, да и на практике чаще нужен весь контур.
         */
        if (kind === 'project') {
            const st = useCadStore.getState();
            const sel = st.selection;
            const bodyId = (sel.kind === 'body' && sel.ids.length > 0)
                ? sel.ids[0]
                : [...st.bodyMeshes.keys()][0];
            if (!bodyId) {
                toast('Нет тела, с которого проецировать. Построй деталь.', 'info');
                setRequestConstraint(null);
                return;
            }
            const edges = getEdgeEndpointsForBody(bodyId);
            const made = projectEdges(edges, sketch.planeRef, newId);
            if (made.length === 0) {
                toast(
                    'На эту плоскость не спроецировалось ни одного ребра — '
                    + 'они перпендикулярны ей. Выбери другую плоскость эскиза.',
                    'info',
                );
                setRequestConstraint(null);
                return;
            }
            for (const np of made) addSketchPrimitive(np);
            setRequestConstraint(null);
            setTimeout(() => solveSketchConstraintsRef.current?.(), 0);
            return;
        }

        if (kind === 'sketchMirror' || kind === 'sketchPattern') {
            if (ids.length === 0) {
                toast('Сначала выдели, что копировать: линии, дуги, окружности.', 'info');
                setRequestConstraint(null);
                return;
            }
            const st = useCadStore.getState();
            const made = kind === 'sketchMirror'
                ? mirrorPrimitives(prims, ids, st.sketchMirrorAxis || 'y', newId)
                : patternPrimitives(
                    prims, ids,
                    st.sketchPatternCount || 3,
                    st.sketchPatternDx ?? 20,
                    st.sketchPatternDy ?? 0,
                    newId,
                );
            if (made.length === 0) {
                toast('Копировать нечего — выдели линии или окружности.', 'info');
                setRequestConstraint(null);
                return;
            }
            for (const np of made) addSketchPrimitive(np);
            setRequestConstraint(null);
            setTimeout(() => solveSketchConstraintsRef.current?.(), 0);
            return;
        }

        if (kind === 'tangent') {
            // Касание — нужны ровно 2 примитива: линия+окружность или
            // окружность+окружность.
            if (ids.length !== 2) {
                console.warn('[sketch] касание: выдели 2 объекта '
                    + '(линия+окружность или окружность+окружность)');
                setRequestConstraint(null);
                return;
            }
            const a = prims.find((p) => p.id === ids[0]);
            const b = prims.find((p) => p.id === ids[1]);
            const ok = a && b
                && ((a.kind === 'line' && b.kind === 'circle')
                 || (a.kind === 'circle' && b.kind === 'line')
                 || (a.kind === 'circle' && b.kind === 'circle'));
            if (!ok) {
                console.warn('[sketch] касание: неподходящая пара объектов');
                setRequestConstraint(null);
                return;
            }
            addSketchConstraint({
                id: newId('cn'), kind: 'tangent', aId: ids[0], bId: ids[1],
            });
            setRequestConstraint(null);
            setTimeout(() => solveSketchConstraintsRef.current?.(), 0);
            return;
        }

        if (kind === 'concentric') {
            // Концентричность — нужны ровно 2 окружности.
            const sel = ids.map((id) => prims.find((p) => p.id === id));
            const circs = sel.filter((p) => p && p.kind === 'circle');
            if (circs.length !== 2) {
                console.warn('[sketch] концентричность: выдели 2 окружности');
                setRequestConstraint(null);
                return;
            }
            addSketchConstraint({
                id: newId('cn'), kind: 'concentric',
                c1Id: circs[0].id, c2Id: circs[1].id,
            });
            setRequestConstraint(null);
            setTimeout(() => solveSketchConstraintsRef.current?.(), 0);
            return;
        }

        if (kind === 'symmetric') {
            // Симметрия — нужны 2 точки + 1 линия (ось симметрии).
            const sel = ids.map((id) => prims.find((p) => p.id === id));
            const pts = sel.filter((p) => p && p.kind === 'point');
            const linesSel = sel.filter((p) => p && p.kind === 'line');
            if (pts.length !== 2 || linesSel.length !== 1) {
                console.warn('[sketch] симметрия: выдели 2 точки и 1 линию-ось');
                setRequestConstraint(null);
                return;
            }
            addSketchConstraint({
                id: newId('cn'), kind: 'symmetric',
                p1Id: pts[0].id, p2Id: pts[1].id, axisLineId: linesSel[0].id,
            });
            setRequestConstraint(null);
            setTimeout(() => solveSketchConstraintsRef.current?.(), 0);
            return;
        }

        if (kind === 'midpoint') {
            // Середина — нужны 1 точка + 1 линия. Точка встанет ровно
            // посередине отрезка.
            const sel = ids.map((id) => prims.find((p) => p.id === id));
            const pts = sel.filter((p) => p && p.kind === 'point');
            const linesSel = sel.filter((p) => p && p.kind === 'line');
            if (pts.length !== 1 || linesSel.length !== 1) {
                console.warn('[sketch] середина: выдели 1 точку и 1 линию');
                setRequestConstraint(null);
                return;
            }
            addSketchConstraint({
                id: newId('cn'), kind: 'midpoint',
                pointId: pts[0].id, lineId: linesSel[0].id,
            });
            setRequestConstraint(null);
            setTimeout(() => solveSketchConstraintsRef.current?.(), 0);
            return;
        }

        if (kind === 'fix') {
            // Фиксация — «прибить» геометрию намертво. fixed не хранится
            // как constraint, а ставится прямо на примитивы: для линии —
            // на обе её точки, для точки — на неё, для окружности — на
            // саму окружность (sketchToGcs учтёт fixed центра).
            if (ids.length === 0) {
                console.warn('[sketch] фиксация: выдели объект(ы)');
                setRequestConstraint(null);
                return;
            }
            const toFix = new Set();      // id точек/окружностей под fixed
            for (const id of ids) {
                const p = prims.find((q) => q.id === id);
                if (!p) continue;
                if (p.kind === 'point' || p.kind === 'circle') {
                    toFix.add(p.id);
                } else if (p.kind === 'line') {
                    toFix.add(p.p1);
                    toFix.add(p.p2);
                } else if (p.kind === 'arc') {
                    toFix.add(p.p1);
                    toFix.add(p.p2);
                }
            }
            for (const id of toFix) {
                updateSketchPrimitive(id, { fixed: true });
            }
            setRequestConstraint(null);
            setTimeout(() => solveSketchConstraintsRef.current?.(), 0);
            return;
        }

        // --- Базовые ограничения: горизонталь / вертикаль ---
        // Применяются к каждой выбранной линии (можно выбрать несколько).
        if (kind === 'horizontal' || kind === 'vertical') {
            const linesSel = ids
                .map((id) => prims.find((p) => p.id === id))
                .filter((p) => p && p.kind === 'line');
            if (linesSel.length === 0) {
                console.warn('[sketch] горизонталь/вертикаль: выдели линию(и)');
                setRequestConstraint(null);
                return;
            }
            for (const l of linesSel) {
                addSketchConstraint({ id: newId('cn'), kind, lineId: l.id });
            }
            setRequestConstraint(null);
            setTimeout(() => solveSketchConstraintsRef.current?.(), 0);
            return;
        }

        // --- Параллельность / перпендикулярность / равенство длин ---
        // Ровно 2 линии.
        if (kind === 'parallel' || kind === 'perpendicular' || kind === 'equal') {
            const linesSel = ids
                .map((id) => prims.find((p) => p.id === id))
                .filter((p) => p && p.kind === 'line');
            if (linesSel.length !== 2) {
                console.warn('[sketch] нужно выделить ровно 2 линии');
                setRequestConstraint(null);
                return;
            }
            // 'equal' в нашем формате хранится как 'equal_length'.
            const cKind = (kind === 'equal') ? 'equal_length' : kind;
            addSketchConstraint({
                id: newId('cn'), kind: cKind,
                line1Id: linesSel[0].id, line2Id: linesSel[1].id,
            });
            setRequestConstraint(null);
            setTimeout(() => solveSketchConstraintsRef.current?.(), 0);
            return;
        }

        // --- Совпадение точек ---
        // Ровно 2 точки → они сольются в одну (p2p_coincident).
        if (kind === 'coincident') {
            const pts = ids
                .map((id) => prims.find((p) => p.id === id))
                .filter((p) => p && p.kind === 'point');
            if (pts.length !== 2) {
                console.warn('[sketch] совпадение: выдели 2 точки');
                setRequestConstraint(null);
                return;
            }
            addSketchConstraint({
                id: newId('cn'), kind: 'coincident',
                p1Id: pts[0].id, p2Id: pts[1].id,
            });
            setRequestConstraint(null);
            setTimeout(() => solveSketchConstraintsRef.current?.(), 0);
            return;
        }

        setRequestConstraint(null);
    }, [requestConstraint, selection, sketch, addSketchConstraint,
        updateSketchPrimitive, setRequestConstraint]);

    // Raycast по всем точкам эскиза — возвращает id точки под курсором
    // (или null). Использует userData.sketchPointId на маркерах точек.
    const pickPointAt = useCallback((clientX, clientY) => {
        if (!groupRef.current) return null;
        const rect = gl.domElement.getBoundingClientRect();
        const ndc = new THREE.Vector2(
            ((clientX - rect.left) / rect.width) * 2 - 1,
            -((clientY - rect.top) / rect.height) * 2 + 1,
        );
        const ray = new THREE.Raycaster();
        // Увеличиваем порог чтобы было удобно попадать
        ray.params.Points = { threshold: 2 };
        ray.setFromCamera(ndc, camera);
        // Собираем все mesh-маркеры точек в группе
        const hits = ray.intersectObject(groupRef.current, true);
        for (const h of hits) {
            const id = h.object.userData?.sketchPointId;
            if (id) return id;
        }
        return null;
    }, [camera, gl]);

    // Raycast по хитбоксам примитивов (линия/окружность) — возвращает
    // id примитива под курсором (или null). Хитбоксы помечены
    // userData.sketchPrimitiveId. Используется DOM-обработчиком
    // onPointerDown, чтобы СРАЗУ (синхронно) понять, попал ли клик в
    // примитив, и выделить его — без гонки с r3f-событием click.
    const pickPrimitiveAt = useCallback((clientX, clientY) => {
        if (!groupRef.current) return null;
        const rect = gl.domElement.getBoundingClientRect();
        const ndc = new THREE.Vector2(
            ((clientX - rect.left) / rect.width) * 2 - 1,
            -((clientY - rect.top) / rect.height) * 2 + 1,
        );
        const ray = new THREE.Raycaster();
        ray.setFromCamera(ndc, camera);
        const hits = ray.intersectObject(groupRef.current, true);
        for (const h of hits) {
            const id = h.object.userData?.sketchPrimitiveId;
            if (id) return id;
        }
        return null;
    }, [camera, gl]);

    // Инструмент «Смещение»: клик по замкнутому профилю создаёт параллельный
    // контур на расстоянии offsetDistance (>0 наружу, <0 внутрь).
    const handleOffsetClick = useCallback((clientX, clientY) => {
        const pt = screenToSketchPlane(camera, gl.domElement, clientX, clientY, groupRef.current);
        if (!pt || !sketch) return;
        const d = useCadStore.getState().offsetDistance || -5;
        const profiles = findProfiles(sketch);
        // Ищем профиль, внутрь которого попал клик.
        const hit = profiles.find((pr) => pointInProfile(pr, pt.x, pt.y, sketch));
        if (!hit) { setSketchStatus({ dof: null, conflicts: [], failed: false }); return; }
        // Окружность: просто новая окружность с r±d.
        if (hit.kind === 'circle') {
            const nr = hit.r + d;
            if (nr <= 0) return;
            addSketchPrimitive({ kind: 'circle', id: newId('c'), cx: hit.cx, cy: hit.cy, r: nr });
            return;
        }
        // Полигон: смещаем вершины и создаём цепочку точек+линий.
        const verts = profilePolygonVertices(hit, sketch);
        const offVerts = offsetPolygonVertices(verts, d);
        if (!offVerts || offVerts.length < 3) return;
        const ptIds = offVerts.map((v) => {
            const id = newId('p');
            addSketchPrimitive({ kind: 'point', id, x: v.x, y: v.y });
            return id;
        });
        for (let i = 0; i < ptIds.length; i++) {
            addSketchPrimitive({
                kind: 'line', id: newId('l'),
                p1: ptIds[i], p2: ptIds[(i + 1) % ptIds.length],
            });
        }
    }, [camera, gl, sketch, addSketchPrimitive]);

    // r3f-onClick по хитбоксу примитива. Обычное выделение примитивов
    // (в т.ч. Shift-мультивыбор) делает DOM-обработчик onPointerDown
    // через pickPrimitiveAt — синхронно, без гонки. Здесь обрабатываем
    // ТОЛЬКО инструмент «Размер»: клик по линии/окружности открывает
    // поле ввода размера.
    const handleSelectId = useCallback((id, withShift) => {
        if (activeTool !== 'dimension') return;
        const prims = sketch.primitives || [];
        const target = prims.find((p) => p.id === id);
        if (!target) return;
        if (target.kind === 'line') {
            const p1 = prims.find((p) => p.id === target.p1);
            const p2 = prims.find((p) => p.id === target.p2);
            if (!p1 || !p2) return;
            const len = Math.hypot(p2.x - p1.x, p2.y - p1.y);
            setDimDraft({
                kind: 'length', targetId: id,
                current: Math.round(len * 100) / 100,
                x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2,
            });
        } else if (target.kind === 'circle') {
            setDimDraft({
                kind: 'diameter', targetId: id,
                current: Math.round(target.r * 2 * 100) / 100,
                x: target.cx, y: target.cy,
            });
        }
    }, [activeTool, sketch, setDimDraft]);

    // Финализация фигуры (используется и из pointerup, и из Enter в input).
    //   start — стартовая точка drag-рисования (не меняется)
    //   end   — конечная точка (либо позиция мыши, либо вычисленная из override)
    //   tool  — тип фигуры
    //   override — null или { width, height, length, radius } из input
    // resolvePoint — переиспользовать существующую точку эскиза в данных
    // координатах (если рядом) либо создать новую. Это замыкает контуры:
    // snap при рисовании ставит точку ровно в существующую → resolvePoint
    // её находит и фигура цепляется к ней, а не плодит точку-дубль
    // (из-за дубля граф контуров не видел замкнутого цикла).
    const resolvePoint = useCallback((px, py) => {
        const cur = useCadStore.getState().doc.sketches
            .find((s) => s.id === sketch.id);
        const pts = (cur?.primitives || []).filter((p) => p.kind === 'point');
        const EPS = 0.6; // допуск совпадения точек, мм
        const hit = pts.find(
            (p) => Math.abs(p.x - px) < EPS && Math.abs(p.y - py) < EPS
        );
        if (hit) return hit.id;
        const np = { kind: 'point', id: newId('p'), x: px, y: py };
        addSketchPrimitive(np);
        return np.id;
    }, [addSketchPrimitive, sketch.id]);

    const finalizeShape = useCallback((tool, start, end, override) => {
        let endX = end.x, endY = end.y;

        // Если есть override — пересчитываем endX/endY чтобы фигура имела
        // ровно заданные размеры в направлении от start к end.
        if (override) {
            const dxRaw = end.x - start.x, dyRaw = end.y - start.y;
            const distRaw = Math.sqrt(dxRaw * dxRaw + dyRaw * dyRaw) || 1;

            if ((tool === 'line' || tool === 'constructionLine')
                    && typeof override.length === 'number') {
                endX = start.x + (dxRaw / distRaw) * override.length;
                endY = start.y + (dyRaw / distRaw) * override.length;
            } else if (tool === 'rectangle') {
                // Сторона направления = знак dxRaw (вправо/влево),
                // высота = знак dyRaw (вверх/вниз).
                const sx = dxRaw >= 0 ? 1 : -1;
                const sy = dyRaw >= 0 ? 1 : -1;
                const w = typeof override.width === 'number' ? override.width : Math.abs(dxRaw);
                const h = typeof override.height === 'number' ? override.height : Math.abs(dyRaw);
                endX = start.x + sx * w;
                endY = start.y + sy * h;
            } else if (tool === 'circle' && typeof override.radius === 'number') {
                // Для окружности direction не важен — радиус строго override
                endX = start.x + override.radius;
                endY = start.y;
            }
        }

        const dx = endX - start.x, dy = endY - start.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 0.5) return;

        const x = endX, y = endY;
        if (tool === 'line' || tool === 'constructionLine') {
            const id1 = resolvePoint(start.x, start.y);
            const id2 = resolvePoint(x, y);
            if (id1 === id2) return; // вырожденная линия в одну точку
            /*
             * Вспомогательная линия — та же линия с признаком construction.
             * Отдельного типа заводить не стали: она участвует в решателе
             * наравне с обычной (к ней привязываются ограничениями), и
             * отличается только тем, что не входит в контур и рисуется
             * пунктиром.
             */
            addSketchPrimitive({
                kind: 'line', id: newId('l'), p1: id1, p2: id2,
                ...(tool === 'constructionLine' ? { construction: true } : null),
            });
        } else if (tool === 'rectangle') {
            const x1 = start.x, y1 = start.y, x2 = x, y2 = y;
            const cornerIds = [
                resolvePoint(x1, y1),
                resolvePoint(x2, y1),
                resolvePoint(x2, y2),
                resolvePoint(x1, y2),
            ];
            for (let i = 0; i < 4; i++) {
                const a = cornerIds[i], b = cornerIds[(i + 1) % 4];
                if (a !== b) {
                    addSketchPrimitive({ kind: 'line', id: newId('l'), p1: a, p2: b });
                }
            }
        } else if (tool === 'circle') {
            addSketchPrimitive({ kind: 'circle', id: newId('c'), cx: start.x, cy: start.y, r: dist });
        } else if (tool === 'ellipse') {
            /*
             * Эллипс: start — центр, end задаёт полуоси. Строим замкнутой
             * полилинией — тогда профили, выдавливание и разбиение работают
             * без спец-логики (тот же приём, что у многоугольника).
             * 64 сегмента: на печати шаг незаметен, точек в эскизе немного.
             */
            const rx = Math.abs(x - start.x), ry = Math.abs(y - start.y);
            if (rx > 0.01 && ry > 0.01) {
                const N = 64;
                const ids = [];
                for (let i = 0; i < N; i++) {
                    const a = (i / N) * Math.PI * 2;
                    ids.push(resolvePoint(start.x + Math.cos(a) * rx, start.y + Math.sin(a) * ry));
                }
                for (let i = 0; i < N; i++) {
                    const p1 = ids[i], p2 = ids[(i + 1) % N];
                    if (p1 !== p2) addSketchPrimitive({ kind: 'line', id: newId('l'), p1, p2 });
                }
            }
        } else if (tool === 'slot') {
            /*
             * Паз — прямоугольник со скруглёнными торцами. start и end задают
             * ось, ширина берётся как треть длины (дальше правится размерами).
             * Нужен для крепежа: в такой паз входит винт с возможностью сдвига.
             */
            const dx = x - start.x, dy = y - start.y;
            const len = Math.hypot(dx, dy);
            if (len > 0.01) {
                const w = Math.max(1, len / 3) / 2;      // полуширина
                const ux = dx / len, uy = dy / len;      // орт вдоль оси
                const nx = -uy, ny = ux;                 // орт поперёк
                const N = 16;                            // сегментов на полукруг
                const pts = [];
                for (let i = 0; i <= N; i++) {           // торец у конца
                    const a = -Math.PI / 2 + (i / N) * Math.PI;
                    pts.push({
                        x: x + (ux * Math.cos(a) + nx * Math.sin(a)) * w,
                        y: y + (uy * Math.cos(a) + ny * Math.sin(a)) * w,
                    });
                }
                for (let i = 0; i <= N; i++) {           // торец у начала
                    const a = Math.PI / 2 + (i / N) * Math.PI;
                    pts.push({
                        x: start.x + (ux * Math.cos(a) + nx * Math.sin(a)) * w,
                        y: start.y + (uy * Math.cos(a) + ny * Math.sin(a)) * w,
                    });
                }
                const ids = pts.map((p) => resolvePoint(p.x, p.y));
                for (let i = 0; i < ids.length; i++) {
                    const p1 = ids[i], p2 = ids[(i + 1) % ids.length];
                    if (p1 !== p2) addSketchPrimitive({ kind: 'line', id: newId('l'), p1, p2 });
                }
            }
        } else if (tool === 'polygon') {
            // Правильный N-угольник: start — центр, end — первая вершина
            // (задаёт радиус и поворот). Остальные вершины — по кругу.
            // Строим как N точек + N линий — тогда контур замкнут графом
            // и его можно выдавить без спец-логики.
            const n = Math.max(3, Math.min(64, polygonSidesRef.current || 6));
            const r = dist;
            const a0 = Math.atan2(y - start.y, x - start.x);
            const vertIds = [];
            for (let i = 0; i < n; i++) {
                const a = a0 + (i / n) * Math.PI * 2;
                vertIds.push(resolvePoint(
                    start.x + Math.cos(a) * r,
                    start.y + Math.sin(a) * r,
                ));
            }
            for (let i = 0; i < n; i++) {
                const p1 = vertIds[i], p2 = vertIds[(i + 1) % n];
                if (p1 !== p2) {
                    addSketchPrimitive({ kind: 'line', id: newId('l'), p1, p2 });
                }
            }
        }
    }, [addSketchPrimitive, resolvePoint]);

    /*
     * ТЕКСТ В ЭСКИЗ — как в 123D Design.
     *
     * Клик по плоскости задаёт левый нижний угол надписи, буквы сразу ложатся
     * в эскиз обычными линиями. Дальше с ними работает всё остальное:
     * профили находятся сами, «Выдавить» делает выпуклую надпись или
     * гравировку, размеры и перемещение — как у любого другого контура.
     *
     * Раньше текст был отдельной операцией «ввёл в панели → получил тело»:
     * человек не видел, где окажется надпись, и не мог её подвинуть.
     */
    const insertTextAt = useCallback(async (x, y) => {
        const st = useCadStore.getState();
        const draft = st.textDraft;
        if (!draft || !draft.text?.trim()) return;
        try {
            const contours = await getGeometryClient()
                .textToContours(draft.text.trim(), draft.fontSize || 10);

            /*
             * ⚠️ ОСЬ Y: контуры приходят из SVG, где Y растёт ВНИЗ, а в эскизе —
             * ВВЕРХ. Без переворота надпись выходит зеркальной по горизонтали
             * (именно так и было: «LOL» читалось наоборот).
             *
             * Заодно выравниваем: клик задаёт ЛЕВЫЙ НИЖНИЙ угол надписи, а не
             * случайную точку внутри букв — считаем габариты и сдвигаем.
             */
            let minX = Infinity, maxY = -Infinity;
            for (const c of contours) {
                for (const p of c) {
                    if (p.x < minX) minX = p.x;
                    if (p.y > maxY) maxY = p.y;
                }
            }
            if (!Number.isFinite(minX)) { minX = 0; maxY = 0; }

            for (const contour of contours) {
                if (!contour || contour.length < 3) continue;
                const ids = contour.map((p) => resolvePoint(
                    x + (p.x - minX),
                    // Переворот по Y + привязка низа надписи к точке клика.
                    y + (maxY - p.y),
                ));
                for (let i = 0; i < ids.length; i++) {
                    const a = ids[i], b = ids[(i + 1) % ids.length];
                    if (a !== b) addSketchPrimitive({ kind: 'line', id: newId('l'), p1: a, p2: b });
                }
            }
            st.setTextDraft(null);
            st.setActiveSketchTool(null);
        } catch (e) {
            console.error('[sketch] text failed:', e);
            toast('Не удалось вставить текст: ' + (e?.message || e), 'error');
        }
    }, [addSketchPrimitive, resolvePoint]);

    // Мост для обработчика клика (он объявлен ниже и берёт функцию из ref).
    const insertTextRef = useRef(insertTextAt);
    insertTextRef.current = insertTextAt;

    // Финализация pendingShape — превращаем в реальные примитивы.
    // Вызывается когда юзер кликнул «✓» рядом с input или нажал Enter.
    const pendingForCommitRef = useRef(null);
    pendingForCommitRef.current = pendingShape;
    const overrideForCommitRef = useRef(null);
    overrideForCommitRef.current = drawSizeOverride;

    const commitPending = useCallback(() => {
        const ps = pendingForCommitRef.current;
        if (!ps) return;
        finalizeShape(ps.tool, ps.start, ps.end, overrideForCommitRef.current);
        setPendingShape(null);
        setDrawSizeOverride(null);
    }, [finalizeShape, setPendingShape, setDrawSizeOverride]);

    // Применить override без commit — обновить только end в pendingShape
    // (визуальный preview обновится).
    const applyOverrideToPending = useCallback((override) => {
        const ps = pendingForCommitRef.current;
        if (!ps || !override) return;
        const dxRaw = ps.end.x - ps.start.x, dyRaw = ps.end.y - ps.start.y;
        const distRaw = Math.sqrt(dxRaw * dxRaw + dyRaw * dyRaw) || 1;
        let endX = ps.end.x, endY = ps.end.y;
        if ((ps.tool === 'line' || ps.tool === 'constructionLine')
                && typeof override.length === 'number') {
            endX = ps.start.x + (dxRaw / distRaw) * override.length;
            endY = ps.start.y + (dyRaw / distRaw) * override.length;
        } else if (ps.tool === 'rectangle') {
            // override.width — это размер по ГОРИЗОНТАЛИ ЭКРАНА.
            // Какая локальная ось эскиза горизонтальна на экране — зависит
            // от плоскости. Чтобы не зависеть от плоскости, вычисляем
            // direction-векторы прямо из screen-проекции.
            //
            // Берём 3 точки в локальной системе: start, start+(1,0), start+(0,1)
            // — проецируем в screen, считаем какая ось локальная даёт
            // горизонтальное смещение, какая — вертикальное.
            const ax = mapLocalAxesToScreen(ps);
            // ax = { hAxis: 'x'|'y', hSign: ±1, vAxis: 'x'|'y', vSign: ±1 }

            // Текущие screen-размеры
            const screenDx = (ax.hAxis === 'x' ? dxRaw : dyRaw) * ax.hSign; // signed по экранной горизонтали
            const screenDy = (ax.vAxis === 'x' ? dxRaw : dyRaw) * ax.vSign; // signed по экранной вертикали
            const sx = screenDx < 0 ? -1 : 1;
            const sy = screenDy < 0 ? -1 : 1;
            const w = typeof override.width  === 'number' ? override.width  : Math.abs(screenDx);
            const h = typeof override.height === 'number' ? override.height : Math.abs(screenDy);

            // Конвертируем (sx*w, sy*h) обратно в локальные dx/dy
            let newDxLocal, newDyLocal;
            if (ax.hAxis === 'x') {
                newDxLocal = sx * w * ax.hSign;
                newDyLocal = sy * h * ax.vSign;
            } else {
                newDxLocal = sy * h * ax.vSign;
                newDyLocal = sx * w * ax.hSign;
            }
            endX = ps.start.x + newDxLocal;
            endY = ps.start.y + newDyLocal;
        } else if (ps.tool === 'circle' && typeof override.radius === 'number') {
            endX = ps.start.x + override.radius;
            endY = ps.start.y;
        }
        updatePendingShapeEnd({ x: endX, y: endY });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updatePendingShapeEnd]);

    // mapLocalAxesToScreen — определяет какая локальная ось эскиза
    // соответствует горизонтали экрана и какая — вертикали.
    // Возвращает { hAxis: 'x'|'y', hSign: ±1, vAxis: 'x'|'y', vSign: ±1 }.
    const mapLocalAxesToScreen = useCallback((ps) => {
        const grp = groupRef.current;
        if (!grp) return { hAxis: 'x', hSign: 1, vAxis: 'y', vSign: 1 };
        const dom = gl.domElement;
        const project = (x, y) => {
            const v = new THREE.Vector3(x, y, 0);
            grp.updateMatrixWorld(true);
            v.applyMatrix4(grp.matrixWorld);
            v.project(camera);
            return { x: v.x, y: -v.y }; // -y чтобы было «экран вверх»
        };
        const O = project(ps.start.x, ps.start.y);
        const X = project(ps.start.x + 1, ps.start.y);
        const Y = project(ps.start.x, ps.start.y + 1);
        // Векторы dX, dY в screen-NDC
        const dXh = X.x - O.x, dXv = X.y - O.y;
        const dYh = Y.x - O.x, dYv = Y.y - O.y;
        // Какая локальная ось «более горизонтальна»?
        const xIsHorizontal = Math.abs(dXh) >= Math.abs(dYh);
        if (xIsHorizontal) {
            return {
                hAxis: 'x', hSign: dXh >= 0 ? 1 : -1,
                vAxis: 'y', vSign: dYv >= 0 ? 1 : -1,
            };
        }
        return {
            hAxis: 'y', hSign: dYh >= 0 ? 1 : -1,
            vAxis: 'x', vSign: dXv >= 0 ? 1 : -1,
        };
    }, [camera, gl]);

    // Конвертация локальных координат эскиза (мм) в screen pixels.
    // Нужно DOM-overlay'ю чтобы прицепить input точно к стороне фигуры.
    const projectToScreen = useCallback((x, y) => {
        if (!groupRef.current) return null;
        const dom = gl.domElement;
        const rect = dom.getBoundingClientRect();
        const v = new THREE.Vector3(x, y, 0);
        groupRef.current.updateMatrixWorld(true);
        v.applyMatrix4(groupRef.current.matrixWorld);
        v.project(camera);
        return {
            x: rect.left + ((v.x + 1) / 2) * rect.width,
            y: rect.top + ((1 - v.y) / 2) * rect.height,
        };
    }, [camera, gl]);

    // Bridge для DOM-input'ов
    useEffect(() => {
        window.__kubikonCommitDraw = commitPending;
        window.__kubikonCancelDraw = () => {
            setPendingShape(null);
            setDrawSizeOverride(null);
        };
        window.__kubikonApplyOverride = applyOverrideToPending;
        window.__kubikonProjectToScreen = projectToScreen;
        return () => {
            delete window.__kubikonCommitDraw;
            delete window.__kubikonCancelDraw;
            delete window.__kubikonApplyOverride;
            delete window.__kubikonProjectToScreen;
        };
    }, [commitPending, applyOverrideToPending, projectToScreen,
        setPendingShape, setDrawSizeOverride]);

    // При смене инструмента или выходе из эскиза — отменяем незавершённое
    const prevToolRef = useRef(activeTool);
    useEffect(() => {
        if (prevToolRef.current !== activeTool && pendingShape) {
            // фиксируем текущее состояние pending (или отменяем — выберем фикс)
            commitPending();
        }
        prevToolRef.current = activeTool;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTool]);

    // ref на текущий pendingShape (чтобы DOM-обработчики видели актуальное)
    const pendingForHandlersRef = useRef(null);
    pendingForHandlersRef.current = pendingShape;
    const downTimeRef = useRef(0);
    const downPosRef = useRef(null); // позиция курсора при pointerdown
    const draggedFlagRef = useRef(false); // был ли реальный drag
    // true — только если МЫ сами вызвали setPointerCapture (drag точки
    // или рисование фигуры). Захват указателя на canvas ломает r3f-
    // событие click по мешам примитивов: r3f v9 сам ведёт pointer
    // capture, чтобы связать pointerdown↔pointerup в один click, а
    // наш release сбрасывал его на КАЖДЫЙ pointerup → клик по линии/
    // окружности не доходил до handleClick. Поэтому capture/release
    // делаем строго в своих ветках и только когда реально захватили.
    const weCapturedRef = useRef(false);

    // Якорные точки для snap при рисовании (как в Fusion). Типы и приоритет:
    //   endpoint (концы линий/дуг, точки эскиза, углы грани) — priority 1
    //   center   (центры окружностей/дуг)                    — priority 2
    //   midpoint (СЕРЕДИНЫ линий эскиза и рёбер грани)        — priority 2
    // Маркеры разной формы: endpoint=квадрат, center=круг, midpoint=треугольник.
    const anchors = useMemo(() => {
        const prims = sketch.primitives || [];
        const pointById = new Map(
            prims.filter((p) => p.kind === 'point').map((p) => [p.id, p])
        );
        const out = [];

        // 1. Точки эскиза = endpoints (несут pointId для переиспользования).
        for (const p of prims) {
            if (p.kind === 'point') {
                out.push({ x: p.x, y: p.y, type: 'endpoint', priority: 1, pointId: p.id });
            }
        }

        // 2. Середины линий эскиза (по концевым точкам p1/p2).
        for (const p of prims) {
            if (p.kind === 'line') {
                const a = pointById.get(p.p1), b = pointById.get(p.p2);
                if (a && b) out.push({
                    x: (a.x + b.x) / 2, y: (a.y + b.y) / 2,
                    type: 'midpoint', priority: 2,
                });
            } else if (p.kind === 'circle') {
                // Центр окружности.
                out.push({ x: p.cx, y: p.cy, type: 'center', priority: 2 });
            } else if (p.kind === 'arc') {
                // Центр дуги + её середина (точка при параметре 0.5).
                out.push({ x: p.cx, y: p.cy, type: 'center', priority: 2 });
                const a = pointById.get(p.p1), b = pointById.get(p.p2);
                if (a && b) {
                    const a0 = Math.atan2(a.y - p.cy, a.x - p.cx);
                    let a1 = Math.atan2(b.y - p.cy, b.x - p.cx);
                    const r = Math.hypot(a.x - p.cx, a.y - p.cy);
                    // Идём в сторону ccw (как хранится у дуги).
                    let span = a1 - a0;
                    if (p.ccw) { while (span <= 0) span += Math.PI * 2; }
                    else       { while (span >= 0) span -= Math.PI * 2; }
                    const mid = a0 + span / 2;
                    out.push({ x: p.cx + Math.cos(mid) * r, y: p.cy + Math.sin(mid) * r,
                               type: 'midpoint', priority: 2 });
                }
            }
        }

        // 3. Углы грани тела (endpoints) + СЕРЕДИНЫ её рёбер (midpoint),
        //    чтобы можно было привязаться к середине стороны параллелепипеда.
        const faceAnchors = getSketchAnchorPoints(sketch.planeRef);
        for (const fa of faceAnchors) {
            out.push({ x: fa.x, y: fa.y, type: 'endpoint', priority: 1 });
        }
        // Середины рёбер грани: между соседними углами контура (замкнутый).
        if (faceAnchors.length >= 2) {
            for (let i = 0; i < faceAnchors.length; i++) {
                const a = faceAnchors[i];
                const b = faceAnchors[(i + 1) % faceAnchors.length];
                out.push({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2,
                           type: 'midpoint', priority: 2 });
            }
        }

        /*
         * 4. ПЕРЕСЕЧЕНИЯ ЛИНИЙ + ЦЕНТР ЗАМКНУТОГО КОНТУРА.
         *
         * 🔴 ЗАЧЕМ. Без этих якорей поставить отверстие РОВНО В ЦЕНТР
         * прямоугольника было нечем, и приходилось идти обходным путём:
         * провести диагональ и целиться в её середину. Но обычная линия
         * через окружность РЕЖЕТ её пополам (findRegions строит области по
         * всем пересечениям) — вместо отверстия в ядро уезжал полукруг,
         * и деталь не получалась. То есть единственный доступный способ
         * попасть в центр сам же ломал построение.
         *
         * Теперь центр ловится напрямую: пересечение двух линий и центр
         * прямоугольной грани — самостоятельные точки привязки.
         *
         * priority 2 — как у midpoint/center: угол (endpoint) при равном
         * расстоянии остаётся главнее.
         */
        const lineSegs = [];
        for (const p of prims) {
            if (p.kind !== 'line') continue;
            const a = pointById.get(p.p1), b = pointById.get(p.p2);
            if (a && b) lineSegs.push([a, b]);
        }
        for (let i = 0; i < lineSegs.length; i++) {
            for (let j = i + 1; j < lineSegs.length; j++) {
                const [a1, a2] = lineSegs[i];
                const [b1, b2] = lineSegs[j];
                const d1x = a2.x - a1.x, d1y = a2.y - a1.y;
                const d2x = b2.x - b1.x, d2y = b2.y - b1.y;
                const den = d1x * d2y - d1y * d2x;
                if (Math.abs(den) < 1e-9) continue;          // параллельны
                const t = ((b1.x - a1.x) * d2y - (b1.y - a1.y) * d2x) / den;
                const u = ((b1.x - a1.x) * d1y - (b1.y - a1.y) * d1x) / den;
                // Пересечение должно лежать НА обоих отрезках, а не на их
                // продолжениях — иначе якоря появлялись бы в пустоте.
                if (t < 0 || t > 1 || u < 0 || u > 1) continue;
                out.push({ x: a1.x + d1x * t, y: a1.y + d1y * t,
                           type: 'center', priority: 2 });
            }
        }

        // Центр прямоугольной грани тела — самый частый случай «в середину».
        if (faceAnchors.length === 4) {
            let sx = 0, sy = 0;
            for (const fa of faceAnchors) { sx += fa.x; sy += fa.y; }
            out.push({ x: sx / 4, y: sy / 4, type: 'center', priority: 2 });
        }

        /*
         * Центр прямоугольника, НАРИСОВАННОГО В ЭСКИЗЕ. Именно этот случай
         * в уроке 2: пластина ещё не выдавлена, грани тела нет, а отверстие
         * уже надо поставить по центру. Диагоналей у такого прямоугольника
         * тоже нет — значит и пересечения линий не помогут.
         *
         * Берём замкнутый контур из четырёх линий: если его углы образуют
         * прямоугольник, центр = среднее углов.
         */
        if (lineSegs.length >= 4) {
            // Уникальные концы всех линий эскиза.
            const key = (p) => Math.round(p.x * 1e6) + ':' + Math.round(p.y * 1e6);
            const seen = new Map();
            for (const [a, b] of lineSegs) {
                for (const p of [a, b]) if (!seen.has(key(p))) seen.set(key(p), p);
            }
            if (seen.size === 4) {
                const pts = [...seen.values()];
                let sx = 0, sy = 0;
                for (const p of pts) { sx += p.x; sy += p.y; }
                const cx = sx / 4, cy = sy / 4;
                // Прямоугольник: все четыре угла равноудалены от центра.
                const d0 = Math.hypot(pts[0].x - cx, pts[0].y - cy);
                const isRect = pts.every(
                    (p) => Math.abs(Math.hypot(p.x - cx, p.y - cy) - d0) < 1e-6);
                if (isRect) out.push({ x: cx, y: cy, type: 'center', priority: 2 });
            }
        }

        return out;
    }, [sketch.planeRef, sketch.primitives]);
    // Активный snap (та точка к которой сейчас примагнитился курсор) —
    // для подсветки маркера в SnapMarkers.
    const [activeSnap, setActiveSnap] = useState(null);
    const anchorsRef = useRef(anchors);
    anchorsRef.current = anchors;

    useEffect(() => {
        const dom = gl.domElement;
        if (!dom || !groupRef.current) return;

        const onPointerDown = (e) => {
            if (e.button !== 0) return;
            if (draggingPointIdRef.current) return;

            // ПРИОРИТЕТ 1: попали в точку → начинаем drag точки.
            // НО только если НЕ активен инструмент рисования: при активном
            // line/rectangle/circle/arc клик по точке должен РИСОВАТЬ
            // (цеплять линию к этой точке — замыкание контура), а не
            // захватывать точку на перетаскивание.
            // pointDragMovedRef=false — пока не было реального движения.
            // Если на pointerup движения так и не было — это был ПРОСТО
            // КЛИК по точке → выделяем её (нужно для ограничений
            // «Симметрия»/«Середина», где выбираются точки).
            if (!activeTool) {
                const pickedPointId = pickPointAt(e.clientX, e.clientY);
                if (pickedPointId) {
                    draggingPointIdRef.current = pickedPointId;
                    pointDragMovedRef.current = false;
                    pointDownPosRef.current = { x: e.clientX, y: e.clientY };
                    pointShiftRef.current = e.shiftKey;
                    try {
                        dom.setPointerCapture(e.pointerId);
                        weCapturedRef.current = true;
                    } catch (_) {}
                    return;
                }
            }

            // ПРИОРИТЕТ 2 (без инструмента): попали в примитив →
            // выделяем его СРАЗУ, синхронно. Раньше выделение делал
            // r3f-onClick меша, а DOM-обработчик отложенно (60мс)
            // снимал его — между этим была гонка: r3f-click мог не
            // успеть, и выделение гасилось. Особенно ломало Shift-
            // мультивыбор. Теперь DOM-слой — единственный источник
            // истины: сам определяет попадание и сам выделяет.
            if (!activeTool) {
                const pickedPrimId = pickPrimitiveAt(e.clientX, e.clientY);
                if (pickedPrimId) {
                    const sel = useCadStore.getState().selection;
                    if (e.shiftKey && sel.kind === 'sketch_primitive') {
                        // Shift — добавить/убрать из набора (toggle).
                        const ids = sel.ids.includes(pickedPrimId)
                            ? sel.ids.filter((x) => x !== pickedPrimId)
                            : [...sel.ids, pickedPrimId];
                        setSelection('sketch_primitive', ids);
                    } else {
                        setSelection('sketch_primitive', [pickedPrimId]);
                    }
                } else {
                    // Клик по пустому месту — снять выделение.
                    clearSelection();
                }
                return;
            }

            /* Инструмент «Текст»: клик задаёт левый нижний угол надписи.
               Буквы сразу становятся линиями эскиза — дальше их можно двигать,
               выдавливать и гравировать как любой другой контур. */
            if (activeTool === 'sketchText') {
                const p = screenToSketchPlane(camera, gl.domElement, e.clientX, e.clientY, groupRef.current);
                if (p) insertTextRef.current(p.x, p.y);
                return;
            }

            /*
             * Инструмент «Скругление» в эскизе: клик по вершине, где сходятся
             * две линии, заменяет угол дугой.
             *
             * Скруглять контур ДО выдавливания удобнее, чем каждое ребро
             * потом: скругление выходит сквозным по всей высоте детали одной
             * операцией.
             */
            if (activeTool === 'sketchFillet') {
                const pid = pickPointAt(e.clientX, e.clientY);
                if (!pid) return;
                const cur = useCadStore.getState().doc.sketches
                    .find((sk) => sk.id === sketch.id);
                if (!cur) return;
                const pair = linesAtPoint(cur, pid);
                if (!pair) {
                    toast('Кликни по вершине, где сходятся ровно две линии.', 'info');
                    return;
                }
                const byId = new Map(cur.primitives
                    .filter((x) => x.kind === 'point').map((x) => [x.id, x]));
                const corner = byId.get(pid);
                const a = byId.get(pair.otherA);
                const b = byId.get(pair.otherB);
                if (!corner || !a || !b) return;
                const radius = useCadStore.getState().sketchFilletRadius || 3;
                const f = computeFillet(corner, a, b, radius);
                if (!f) {
                    toast(
                        'Скругление радиусом ' + radius + ' мм здесь не помещается — '
                        + 'уменьши радиус или удлини стороны.',
                        'info',
                    );
                    return;
                }
                /*
                 * Вершину НЕ удаляем: на неё могут ссылаться ограничения.
                 * Вместо этого добавляем две точки касания, переносим на них
                 * концы линий и соединяем дугой — контур остаётся замкнутым.
                 */
                const id1 = newId('p');
                const id2 = newId('p');
                addSketchPrimitive({ kind: 'point', id: id1, x: f.t1.x, y: f.t1.y });
                addSketchPrimitive({ kind: 'point', id: id2, x: f.t2.x, y: f.t2.y });
                updateSketchPrimitive(pair.lineA.id,
                    pair.lineA.p1 === pid ? { p1: id1 } : { p2: id1 });
                updateSketchPrimitive(pair.lineB.id,
                    pair.lineB.p1 === pid ? { p1: id2 } : { p2: id2 });
                addSketchPrimitive({
                    kind: 'arc', id: newId('a'),
                    p1: id1, p2: id2,
                    cx: f.center.x, cy: f.center.y,
                    ccw: f.ccw,
                });
                removeSketchPrimitive(pid);
                setTimeout(() => solveSketchConstraintsRef.current?.(), 0);
                return;
            }

            /*
             * Инструмент «Продлить»: клик по линии ближе к нужному концу —
             * он дотягивается до ближайшей линии на своём пути.
             *
             * Парный к «Обрезать»: там убирают лишнее, здесь дотягивают
             * недостающее. Нужен, когда контур почти замкнут, но линии не
             * достают друг до друга — выдавить такой эскиз нельзя.
             */
            if (activeTool === 'extendSketch') {
                const lid = pickPrimitiveAt(e.clientX, e.clientY);
                if (!lid) return;
                const cur = useCadStore.getState().doc.sketches
                    .find((sk) => sk.id === sketch.id);
                if (!cur) return;
                const line = cur.primitives.find(
                    (x) => x.id === lid && x.kind === 'line');
                if (!line) {
                    toast('Продлить можно только линию.', 'info');
                    return;
                }
                const pt = screenToSketchPlane(
                    camera, gl.domElement, e.clientX, e.clientY, groupRef.current);
                if (!pt) return;
                const byId = new Map(cur.primitives
                    .filter((x) => x.kind === 'point').map((x) => [x.id, x]));
                const others = cur.primitives.filter(
                    (x) => x.kind === 'line' && x.id !== lid);
                const end = nearestEnd(line, byId, pt.x, pt.y);
                const target = computeExtend(line, end, byId, others);
                if (!target) {
                    toast(
                        'Впереди нет линии, до которой можно дотянуть. '
                        + 'Кликни ближе к тому концу, который нужно продлить.',
                        'info',
                    );
                    return;
                }
                updateSketchPrimitive(
                    end === 'p1' ? line.p1 : line.p2,
                    { x: target.x, y: target.y },
                );
                setTimeout(() => solveSketchConstraintsRef.current?.(), 0);
                return;
            }

            // Инструмент «Обрезать» (trim): клик по примитиву удаляет его.
            // Простой предсказуемый trim — «убери эту линию/дугу/окружность».
            if (activeTool === 'trim') {
                const pid = pickPrimitiveAt(e.clientX, e.clientY);
                if (pid) {
                    removeSketchPrimitive(pid);
                    setTimeout(() => solveSketchConstraintsRef.current?.(), 0);
                }
                return;
            }

            // Инструмент «Смещение» (offset): клик по замкнутому профилю →
            // создаём параллельный контур на расстоянии offsetDistance.
            if (activeTool === 'offsetSketch') {
                handleOffsetClick(e.clientX, e.clientY);
                return;
            }

            const pt = screenToSketchPlane(
                camera, dom, e.clientX, e.clientY, groupRef.current,
                { anchors: anchorsRef.current, snapPx: 12 },
            );
            if (!pt) return;
            // Если был snap — берём якорь как есть (без round). Иначе round до мм.
            const x = pt.snapped ? pt.x : snap(pt.x);
            const y = pt.snapped ? pt.y : snap(pt.y);

            // Точка — простая постановка
            /*
             * Сплайн: каждый клик добавляет опорную точку. Кривая ляжет
             * ЧЕРЕЗ них — человек ставит точку там, где хочет видеть обвод.
             */
            if (activeTool === 'spline') {
                const cur = splineDraftRef.current || [];
                setSplineDraft([...cur, { x, y }]);
                return;
            }

            if (activeTool === 'point') {
                addSketchPrimitive({ kind: 'point', id: newId('p'), x, y });
                return;
            }

            // Дуга — три клика: центр → начало → конец.
            if (activeTool === 'arc') {
                const d = arcDraftRef.current;
                if (!d) {
                    // 1-й клик — центр
                    setArcDraft({ center: { x, y } });
                } else if (!d.startPt) {
                    // 2-й клик — начальная точка (задаёт радиус)
                    setArcDraft({ center: d.center, startPt: { x, y } });
                } else {
                    // 3-й клик — конечная точка. Конец «примагничиваем»
                    // на радиус (расстояние от центра до начала).
                    const { center, startPt } = d;
                    const r = Math.hypot(startPt.x - center.x, startPt.y - center.y);
                    const ang = Math.atan2(y - center.y, x - center.x);
                    const endX = center.x + Math.cos(ang) * r;
                    const endY = center.y + Math.sin(ang) * r;
                    if (r > 0.5) {
                        // Направление обхода дуги (ccw — против часовой).
                        // Без него тесселяция профиля «срезала бы» дугу в
                        // кратчайшую сторону → контур вырождался. Определяем
                        // по знаку поворота от начала к 3-му клику: куда
                        // пользователь вёл, та сторона и берётся.
                        const aStart = Math.atan2(startPt.y - center.y,
                                                  startPt.x - center.x);
                        let dAng = ang - aStart;
                        while (dAng <= -Math.PI) dAng += Math.PI * 2;
                        while (dAng > Math.PI) dAng += -Math.PI * 2;
                        const ccw = dAng >= 0;
                        // resolvePoint — переиспользует точки эскиза, если
                        // концы дуги попали в существующие (замыкание
                        // контура с линиями и наоборот).
                        const id1 = resolvePoint(startPt.x, startPt.y);
                        const id2 = resolvePoint(endX, endY);
                        if (id1 !== id2) {
                            addSketchPrimitive({
                                kind: 'arc', id: newId('a'),
                                p1: id1, p2: id2,
                                cx: center.x, cy: center.y,
                                ccw,   // направление обхода p1→p2
                            });
                        }
                    }
                    setArcDraft(null);
                    setArcHover(null);
                }
                return;
            }

            // Рисование фигуры. Логика:
            //   - Если pendingShape уже есть и phase='click' → это второй клик,
            //     фиксируем end и переводим в phase='placed' (input'ы остаются).
            //   - Если pendingShape нет → стартуем новый pendingShape в phase='drag'.
            const cur = pendingForHandlersRef.current;
            if (cur && cur.tool === activeTool && cur.phase === 'click') {
                // Второй клик — закрепляем end по позиции мыши
                updatePendingShapeEnd({ x, y });
                updatePendingShapePhase('placed');
                return;
            }
            if (cur && cur.tool === activeTool && cur.phase === 'placed') {
                // Если уже placed — стартуем новую фигуру (commit предыдущей)
                commitPending();
            }

            setPendingShape({
                tool: activeTool,
                start: { x, y },
                end: { x, y },
                phase: 'drag',
            });
            setDrawSizeOverride(null);
            downTimeRef.current = performance.now();
            downPosRef.current = { x: e.clientX, y: e.clientY };
            draggedFlagRef.current = false;
            try {
                dom.setPointerCapture(e.pointerId);
                weCapturedRef.current = true;
            } catch (_) {}
        };

        const onPointerMove = (e) => {
            /*
             * Опорная точка для выравнивания по осям — начало текущей фигуры
             * (или центр дуги). Пока фигура не начата, выравнивать не от чего.
             */
            const draft = pendingForHandlersRef.current;
            const axisFrom = (draft && draft.phase !== 'placed' && draft.start)
                ? draft.start
                : (arcDraftRef.current || null);
            const pt = screenToSketchPlane(
                camera, dom, e.clientX, e.clientY, groupRef.current,
                { anchors: anchorsRef.current, snapPx: 12, axisFrom, axisPx: 8 },
            );
            if (!pt) return;
            // Обновляем подсветку якорной точки (для маркера)
            setActiveSnap(pt.snapped || null);
            const x = pt.snapped ? pt.x : snap(pt.x);
            const y = pt.snapped ? pt.y : snap(pt.y);

            // Drag точки. Точку реально двигаем только после того, как
            // курсор сместился больше порога (4px) — иначе обычный клик
            // с микро-дрожанием мыши сдвинул бы точку. Пока сдвига нет,
            // это «кандидат на клик» (выделение точки на pointerup).
            if (draggingPointIdRef.current) {
                if (!pointDragMovedRef.current && pointDownPosRef.current) {
                    const dx = e.clientX - pointDownPosRef.current.x;
                    const dy = e.clientY - pointDownPosRef.current.y;
                    if (Math.sqrt(dx * dx + dy * dy) > 4) {
                        pointDragMovedRef.current = true;
                    }
                }
                if (pointDragMovedRef.current) {
                    updateSketchPrimitive(draggingPointIdRef.current, { x, y });
                    solveSketchAt(draggingPointIdRef.current, x, y);
                }
                return;
            }

            // Дуга в процессе рисования — обновляем позицию для preview.
            if (activeTool === 'arc' && arcDraftRef.current) {
                setArcHover({ x, y });
                return;
            }

            // Обновление pendingShape:
            //   - phase='drag': обновляем end, фиксируем что был реальный drag
            //   - phase='click': обновляем end (юзер ведёт мышью между кликами)
            //   - phase='placed': игнорируем (рисование закончено, ждём commit)
            const cur = pendingForHandlersRef.current;
            if (!cur) return;
            if (cur.phase === 'placed') return;

            updatePendingShapeEnd({ x, y });
            if (cur.phase === 'drag' && downPosRef.current) {
                const dx = e.clientX - downPosRef.current.x;
                const dy = e.clientY - downPosRef.current.y;
                if (Math.sqrt(dx*dx + dy*dy) > 4) draggedFlagRef.current = true;
            }
        };

        // Освобождаем захват указателя ТОЛЬКО если его ставили мы сами.
        // Безусловный release ломал r3f-click по примитивам (см. коммент
        // у weCapturedRef).
        const releaseIfOurs = (e) => {
            if (!weCapturedRef.current) return;
            weCapturedRef.current = false;
            try { dom.releasePointerCapture(e.pointerId); } catch (_) {}
        };

        const onPointerUp = (e) => {
            if (e.button !== 0) return;
            if (draggingPointIdRef.current) {
                const ptId = draggingPointIdRef.current;
                draggingPointIdRef.current = null;
                releaseIfOurs(e);
                // Движения не было → это был клик по точке → выделяем её.
                // Точки тоже хранятся в selection 'sketch_primitive' —
                // это позволяет выбирать точки для ограничений
                // «Симметрия» (2 точки + ось) и «Середина» (точка + линия).
                if (!pointDragMovedRef.current) {
                    const sel = useCadStore.getState().selection;
                    if (pointShiftRef.current
                        && sel.kind === 'sketch_primitive') {
                        const ids = sel.ids.includes(ptId)
                            ? sel.ids.filter((x) => x !== ptId)
                            : [...sel.ids, ptId];
                        setSelection('sketch_primitive', ids);
                    } else {
                        setSelection('sketch_primitive', [ptId]);
                    }
                }
                return;
            }
            releaseIfOurs(e);

            const cur = pendingForHandlersRef.current;
            if (!cur) return;
            if (cur.phase !== 'drag') return;

            // Решаем: реально тащили или просто кликнули?
            const elapsed = performance.now() - downTimeRef.current;
            if (draggedFlagRef.current && elapsed > 100) {
                // Был drag → переводим в 'placed' (input'ы остаются для редактирования)
                updatePendingShapePhase('placed');
            } else {
                // Был просто клик → переводим в 'click' (ждём второго клика)
                updatePendingShapePhase('click');
            }
        };

        dom.addEventListener('pointerdown', onPointerDown);
        dom.addEventListener('pointermove', onPointerMove);
        dom.addEventListener('pointerup', onPointerUp);
        dom.addEventListener('pointercancel', onPointerUp);
        return () => {
            dom.removeEventListener('pointerdown', onPointerDown);
            dom.removeEventListener('pointermove', onPointerMove);
            dom.removeEventListener('pointerup', onPointerUp);
            dom.removeEventListener('pointercancel', onPointerUp);
        };
    }, [
        camera, gl, activeTool, addSketchPrimitive, updateSketchPrimitive,
        removeSketchPrimitive, handleOffsetClick,
        solveSketchAt, clearSelection, setDrawSizeOverride,
        pickPointAt, pickPrimitiveAt, setSelection,
        setPendingShape, updatePendingShapeEnd,
        updatePendingShapePhase, commitPending, resolvePoint,
    ]);

    const planeId = sketch.planeRef?.plane || 'XY';
    const planeOffset = sketch.planeRef?.offset || 0;
    const planeRefForBasis = sketch.planeRef;
    const planePos = useMemo(
        () => planeWorldPosition(planeId, planeOffset, planeRefForBasis),
        [planeId, planeOffset, planeRefForBasis]
    );
    const quat = useMemo(
        () => planeQuaternion(planeId, planeRefForBasis),
        [planeId, planeRefForBasis]
    );
    const primitives = sketch.primitives || [];

    return (
        <group ref={groupRef} position={planePos} quaternion={quat}>
            <SketchGrid size={400} step={10} majorStep={50}/>
            {/* Маркеры привязок: лёгкие endpoint-ориентиры + активный
                глиф захвата по типу (квадрат/треугольник/круг). */}
            <SnapMarkers
                anchors={anchors.filter((a) => !a.pointId)}
                activeSnap={activeSnap}
            />
            <SketchProfiles sketch={sketch} isPickerActive={false}/>
            {primitives.map((p) => (
                <Primitive
                    key={p.id}
                    primitive={p}
                    allPrimitives={primitives}
                    interactive
                    onSelectId={handleSelectId}
                />
            ))}
            {/*
              * ⚠️ ПОРЯДОК ЗДЕСЬ ВАЖЕН.
              *
              * DimensionLabels и DimensionInput содержат <Html> из drei — он
              * вставляет свой DOM-узел в обход React-дерева (портал в canvas'
              * контейнер). Если ПЕРЕД таким узлом появляются новые соседи
              * (DrawPreview при начале рисования, ArcDraftPreview), React
              * теряет позицию вставки и падает с
              * «insertBefore: узел не является дочерним» — а следом умирает
              * весь редактор и теряется WebGL-контекст.
              *
              * Поэтому всё, что содержит <Html>, идёт ПЕРВЫМ и с постоянной
              * позицией: перед ним ничего не появляется и не исчезает.
              * Меняющиеся превью — строго после.
              */}
            <DimensionLabels sketch={sketch} onEdit={editDimension}/>
            {dimDraft && (
                <DimensionInput
                    draft={dimDraft}
                    onApply={applyDimension}
                    onCancel={() => setDimDraft(null)}
                />
            )}
            {pendingShape && (
                <DrawPreview
                    tool={pendingShape.tool}
                    start={pendingShape.start}
                    current={pendingShape.end}
                    polygonSides={polygonSides}
                />
            )}
            {arcDraft && (
                <ArcDraftPreview draft={arcDraft} hover={arcHover}/>
            )}
        </group>
    );
};


// ===== Пассивный эскиз (только просмотр, без интеракции) =====
const PassiveSketch = ({ sketch, isPickerActive }) => {
    const planeId = sketch.planeRef?.plane || 'XY';
    const planeOffset = sketch.planeRef?.offset || 0;
    const planeRefForBasis = sketch.planeRef;
    const planePos = useMemo(
        () => planeWorldPosition(planeId, planeOffset, planeRefForBasis),
        [planeId, planeOffset, planeRefForBasis]
    );
    const quat = useMemo(
        () => planeQuaternion(planeId, planeRefForBasis),
        [planeId, planeRefForBasis]
    );
    const primitives = sketch.primitives || [];
    return (
        <group position={planePos} quaternion={quat}>
            <SketchProfiles sketch={sketch} isPickerActive={isPickerActive}/>
            {primitives.map((p) => (
                <Primitive key={p.id} primitive={p} allPrimitives={primitives} interactive={false}/>
            ))}
        </group>
    );
};


// ===== Главный overlay =====
const SketchOverlay = () => {
    const sketches = useCadStore((s) => s.doc.sketches);
    const activeSketchId = useCadStore((s) => s.activeSketchId);
    const mode = useCadStore((s) => s.mode);
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const sketchVisibility = useCadStore((s) => s.sketchVisibility);

    // drawState — состояние preview drag-рисования (хранится тут)
    const [drawState, setDrawState] = useState(null);

    // Профили эскиза интерактивны в 3D ВСЕГДА (как в Fusion): наведи →
    // подсветка, клик → выбор + контекстное меню (Выдавить/Вращение/...).
    // Плюс во время операции extrude/revolve/sweep/loft клик уходит в неё.
    const isProfilePickerActive = (mode === 'view3d');

    return (
        <>
            {sketches.map((sk) => {
                const isActive = mode === 'sketch' && sk.id === activeSketchId;
                // Видимость управляется Browser'ом. Активный эскиз всегда виден
                // (иначе нельзя рисовать). Default: true.
                const visible = isActive
                    || (sketchVisibility.has(sk.id) ? sketchVisibility.get(sk.id) : true);
                if (!visible) return null;
                return isActive ? (
                    <ActiveSketch
                        key={sk.id}
                        sketch={sk}
                        drawState={drawState}
                        setDrawState={setDrawState}
                    />
                ) : (
                    <PassiveSketch
                        key={sk.id}
                        sketch={sk}
                        isPickerActive={isProfilePickerActive}
                    />
                );
            })}
        </>
    );
};

export default SketchOverlay;
